import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import axios from "axios";
import BandejaDocumentosSoporte from "./BandejaDocumentosSoporte";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter,
  DialogClose
} from "../components/ui/dialog";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { 
  Calculator,
  FileText,
  Briefcase,
  Plus,
  Pencil,
  Trash2,
  Ban,
  Search,
  RefreshCw,
  Download,
  FileSpreadsheet,
  FileJson,
  FileType,
  File,
  Upload,
  Check,
  ChevronsUpDown,
  X,
  Printer
} from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { cn } from "../lib/utils";
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { saveAs } from 'file-saver';

const submoduloConfig = {
  "datos-declaracion": { 
    title: "Datos de la Declaración", 
    icon: FileText, 
    color: "pink",
    description: "Gestión de declaraciones tributarias"
  },
  "documentos-soporte": { 
    title: "Cargar Documentos Soporte", 
    icon: Briefcase, 
    color: "purple",
    description: "Carga y gestión de documentos de soporte"
  }
};

const LiquidacionesPage = ({ portalMode = false }) => {
  const { submodulo: routeSubmodulo } = useParams();
  const submodulo = portalMode ? "datos-declaracion" : routeSubmodulo;
  const navigate = useNavigate();
  const { API, token, user } = useAuth();
  const isPortal = portalMode || user?.role === "contribuyente";
  const useBandejaPortal = !isPortal && submodulo === "documentos-soporte";

  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [data, setData] = useState([]);

  // Multi-filter states for datos-declaracion
  const [filters, setFilters] = useState({
    nro_planilla: "",
    clase_contribuyente: "",
    rif: "",
    nombre_razon_social: "",
    lae: "",
    codigo_catastral: "",
    fecha: "",
    mes_anio_declaracion: "",
    estatus_pago: "",
    estatus: "",
    oficina: ""
  });

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  
  // Dialog de confirmación para ingreso bruto vacío
  const [confirmIngresoBrutoOpen, setConfirmIngresoBrutoOpen] = useState(false);
  const [pendingSaveAction, setPendingSaveAction] = useState(null); // 'create' o 'update'

  // RIF dropdown states
  const [contribuyentes, setContribuyentes] = useState([]);
  const [rifPopoverOpen, setRifPopoverOpen] = useState(false);
  const [loadingContribuyentes, setLoadingContribuyentes] = useState(false);

  // Tipos de impuestos states
  const [tiposImpuesto, setTiposImpuesto] = useState([]);
  const [tiposImpuestoOpen, setTiposImpuestoOpen] = useState(false);
  const [tiposImpuestoSearch, setTiposImpuestoSearch] = useState("");
  const [oficinas, setOficinas] = useState([]);
  const [oficinaFiltro, setOficinaFiltro] = useState("todas");
  
  // Datos adicionales para cálculo automático
  const [tiposCalculo, setTiposCalculo] = useState([]);
  const [cotizacionActiva, setCotizacionActiva] = useState(null);
  const [actividadesEconomicas, setActividadesEconomicas] = useState([]);
  
  // Estado para cálculo de mes/descuento/multa de contribuyentes con LAE
  const [calculoMesPago, setCalculoMesPago] = useState(null);
  const [loadingCalculoMes, setLoadingCalculoMes] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };
  const currentConfig = submoduloConfig[submodulo] || null;

  // Fetch data based on submodulo
  const fetchData = async () => {
    if (!submodulo) return;
    setLoading(true);
    try {
      const endpoint = getEndpoint(submodulo);
      const response = await axios.get(`${API}/liquidaciones/${endpoint}`, { headers });
      setData(response.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      setData([]);
    }
    setLoading(false);
  };

  // Fetch tipos de impuesto
  const fetchTiposImpuesto = async () => {
    try {
      const response = await axios.get(`${API}/definiciones/tipos-impuesto`, { headers });
      setTiposImpuesto(response.data);
    } catch (error) {
      console.error("Error fetching tipos de impuesto:", error);
    }
  };

  // Fetch tipos de cálculo
  const fetchTiposCalculo = async () => {
    try {
      const response = await axios.get(`${API}/definiciones/tipos-calculo`, { headers });
      setTiposCalculo(response.data);
    } catch (error) {
      console.error("Error fetching tipos de cálculo:", error);
    }
  };

  // Fetch cotización moneda activa
  const fetchCotizacionActiva = async () => {
    try {
      const response = await axios.get(`${API}/definiciones/cotizaciones`, { headers });
      // Buscar la cotización con estatus "Activo"
      const activa = response.data.find(c => c.estatus === "Activo");
      setCotizacionActiva(activa);
      console.log("Cotización activa cargada:", activa);
    } catch (error) {
      console.error("Error fetching cotización moneda:", error);
    }
  };

  // Fetch oficinas para filtro
  const fetchOficinas = async () => {
    try {
      const response = await axios.get(`${API}/definiciones/oficinas`, { headers });
      setOficinas(response.data.filter(o => o.is_active !== false));
    } catch (error) {
      console.error("Error fetching oficinas:", error);
    }
  };

  // Fetch actividades económicas
  const fetchActividadesEconomicas = async () => {
    try {
      const response = await axios.get(`${API}/definiciones/actividades-economicas`, { headers });
      setActividadesEconomicas(response.data);
    } catch (error) {
      console.error("Error fetching actividades económicas:", error);
    }
  };
  
  // Calcular mes de pago, descuento y multa para contribuyente con LAE
  const calcularMesPagoContribuyente = async (contribuyenteId) => {
    if (!contribuyenteId) {
      setCalculoMesPago(null);
      return null;
    }
    
    setLoadingCalculoMes(true);
    try {
      const response = await axios.get(`${API}/liquidaciones/calcular-mes-pago/${contribuyenteId}`, { headers });
      setCalculoMesPago(response.data);
      return response.data;
    } catch (error) {
      console.error("Error calculando mes de pago:", error);
      setCalculoMesPago(null);
      return null;
    } finally {
      setLoadingCalculoMes(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchTiposImpuesto();
    fetchTiposCalculo();
    fetchCotizacionActiva();
    fetchOficinas();
    fetchActividadesEconomicas();
  }, [submodulo]);

  // Fetch contribuyentes when clase_contribuyente changes
  const fetchContribuyentes = async (clase) => {
    if (!clase) {
      setContribuyentes([]);
      return;
    }
    
    setLoadingContribuyentes(true);
    try {
      // Map clase name to API parameter
      const claseParam = clase === "Contribuyente con LAE" ? "con-lae" : "sin-lae";
      const response = await axios.get(`${API}/contribuyentes/buscar?clase=${claseParam}`, { headers });
      setContribuyentes(response.data);
    } catch (error) {
      console.error("Error fetching contribuyentes:", error);
      setContribuyentes([]);
    }
    setLoadingContribuyentes(false);
  };

  // Effect to reload contribuyentes when clase changes
  // Track if we're in initial edit mode to avoid clearing RIF data
  const [initialEditLoad, setInitialEditLoad] = useState(false);
  
  useEffect(() => {
    if (formData.clase_contribuyente) {
      fetchContribuyentes(formData.clase_contribuyente);
      
      // Only clear RIF selection when clase changes during creation or manual change (not initial edit load or portal mode)
      if (!initialEditLoad && !isPortal) {
        const updates = { rif: "", contribuyente_id: "", contribuyente_nombre: "", nombre_razon_social: "" };
        
        // Si es Contribuyente sin LAE, establecer mes/año actual y multa en 0
        if (formData.clase_contribuyente === "Contribuyente sin LAE") {
          const now = new Date();
          const year = now.getFullYear();
          const month = String(now.getMonth() + 1).padStart(2, '0');
          updates.mes_anio_declaracion = `${year}-${month}`;
          updates.multa = 0;
        }
        
        setFormData(prev => ({ ...prev, ...updates }));
      } else {
        // Reset the flag after initial edit load
        setInitialEditLoad(false);
      }
    }
  }, [formData.clase_contribuyente]);

  // Calcular Total automáticamente: Sub Total - Descuento - Reintegro - Retención + Multa
  // Y calcular Divisas ($) = Total / Dólar y Divisas (€) = Total / Euro (cotización activa)
  useEffect(() => {
    if (submodulo === "datos-declaracion") {
      // Sumar los sub_total de todas las líneas de impuestos
      const subTotalSum = (formData.lineas_impuestos || []).reduce(
        (acc, linea) => acc + (parseFloat(linea.sub_total) || 0), 0
      );
      const descuento = parseFloat(formData.descuento) || 0;
      const reintegro = parseFloat(formData.reintegro) || 0;
      const retencion = parseFloat(formData.retencion) || 0;
      const multa = parseFloat(formData.multa) || 0;
      
      const nuevoTotal = subTotalSum - descuento - reintegro - retencion + multa;
      
      // Calcular Divisas ($) = Total / Dólar (cotización activa)
      const dolarActivo = cotizacionActiva?.dolar || 0;
      const nuevasDivisas = dolarActivo > 0 ? nuevoTotal / dolarActivo : 0;
      
      // Calcular Divisas (€) = Total / Euro (cotización activa)
      const euroActivo = cotizacionActiva?.euro || 0;
      const nuevasDivisasEuro = euroActivo > 0 ? nuevoTotal / euroActivo : 0;
      
      // Solo actualizar si los valores son diferentes
      const updates = {};
      if (formData.total !== nuevoTotal) {
        updates.total = nuevoTotal;
      }
      if (formData.divisas !== parseFloat(nuevasDivisas.toFixed(2))) {
        updates.divisas = parseFloat(nuevasDivisas.toFixed(2));
      }
      if (formData.divisas_euro !== parseFloat(nuevasDivisasEuro.toFixed(2))) {
        updates.divisas_euro = parseFloat(nuevasDivisasEuro.toFixed(2));
      }
      
      if (Object.keys(updates).length > 0) {
        setFormData(prev => ({ ...prev, ...updates }));
      }
    }
  }, [formData.lineas_impuestos, formData.descuento, formData.reintegro, formData.retencion, formData.multa, submodulo, cotizacionActiva]);

  // Verificar si hay campos de ingreso bruto vacíos
  const tieneIngresoBrutoVacio = () => {
    if (submodulo === "datos-declaracion" && formData.lineas_impuestos?.length > 0) {
      return formData.lineas_impuestos.some(
        linea => linea.ingreso_bruto === null || linea.ingreso_bruto === undefined || linea.ingreso_bruto === ""
      );
    }
    return false;
  };

  // Llenar campos de ingreso bruto vacíos con 0 y recalcular
  const llenarIngresoBrutoConCero = () => {
    const lineasActualizadas = formData.lineas_impuestos.map(linea => {
      if (linea.ingreso_bruto === null || linea.ingreso_bruto === undefined || linea.ingreso_bruto === "") {
        const importe = linea.importe || 0;
        const esActividadEconomica = linea.actividad_economica_id;
        
        // Calcular sub_total con ingreso bruto = 0
        let nuevoSubTotal = 0;
        
        // Aplicar mínimo tributable para actividades económicas
        if (esActividadEconomica) {
          const minimoTributable = linea.minimo_tributable || 0;
          const valorUnidadCuenta = cotizacionActiva?.valor_unidad_cuenta || 0;
          const minimoTributableReal = minimoTributable * valorUnidadCuenta;
          nuevoSubTotal = minimoTributableReal;
        }
        
        return {
          ...linea,
          ingreso_bruto: 0,
          sub_total: nuevoSubTotal
        };
      }
      return linea;
    });
    
    return lineasActualizadas;
  };

  // Confirmar agregar valor 0 y guardar
  const confirmarAgregarCeroYGuardar = async () => {
    const lineasActualizadas = llenarIngresoBrutoConCero();
    const formDataActualizado = { ...formData, lineas_impuestos: lineasActualizadas };
    
    // Limpiar campos de archivos antes de enviar
    delete formDataActualizado.archivo_libro_diario;
    delete formDataActualizado.archivo_declaracion_iva;
    delete formDataActualizado.archivo_otros;
    delete formDataActualizado.uploading_libro_diario;
    delete formDataActualizado.uploading_declaracion_iva;
    delete formDataActualizado.uploading_otros;
    
    setConfirmIngresoBrutoOpen(false);
    
    try {
      const endpoint = getEndpoint(submodulo);
      
      if (pendingSaveAction === 'create') {
        const res = await axios.post(`${API}/liquidaciones/${endpoint}`, formDataActualizado, { headers });
        
        // Portal: subir archivos después de crear
        const declaracionId = res.data?.id;
        if (isPortal && declaracionId) {
          const tiposArchivo = ["libro_diario", "declaracion_iva", "otros"];
          for (const tipo of tiposArchivo) {
            const file = formData[`archivo_${tipo}`];
            if (file && file.name && file.size) {
              try {
                const fd = new FormData();
                fd.append("file", file);
                await axios.post(
                  `${API}/portal/declaracion/${declaracionId}/upload/${tipo}`,
                  fd,
                  { headers: { ...headers, "Content-Type": "multipart/form-data" } }
                );
              } catch (uploadErr) {
                console.error(`Error subiendo ${tipo}:`, uploadErr);
              }
            }
          }
        }
        
        toast.success("Registro creado exitosamente");
      } else {
        await axios.put(`${API}/liquidaciones/${endpoint}/${editingItem.id}`, formDataActualizado, { headers });
        toast.success("Registro actualizado exitosamente");
      }
      
      setDialogOpen(false);
      setEditingItem(null);
      setFormData({});
      setPendingSaveAction(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar registro");
    }
  };

  // CRUD operations
  const handleCreate = async () => {
    // Verificar si hay ingreso bruto vacío
    if (tieneIngresoBrutoVacio()) {
      setPendingSaveAction('create');
      setConfirmIngresoBrutoOpen(true);
      return;
    }
    
    try {
      const endpoint = getEndpoint(submodulo);
      // Limpiar campos de archivos del formData antes de enviar al backend
      const dataToSend = { ...formData };
      delete dataToSend.archivo_libro_diario;
      delete dataToSend.archivo_declaracion_iva;
      delete dataToSend.archivo_otros;
      delete dataToSend.uploading_libro_diario;
      delete dataToSend.uploading_declaracion_iva;
      delete dataToSend.uploading_otros;

      const res = await axios.post(`${API}/liquidaciones/${endpoint}`, dataToSend, { headers });
      const declaracionId = res.data?.id;
      
      // Portal: subir archivos al Object Storage si hay seleccionados
      if (isPortal && declaracionId) {
        const tiposArchivo = ["libro_diario", "declaracion_iva", "otros"];
        for (const tipo of tiposArchivo) {
          const file = formData[`archivo_${tipo}`];
          if (file && file.name && file.size) {
            try {
              const fd = new FormData();
              fd.append("file", file);
              await axios.post(
                `${API}/portal/declaracion/${declaracionId}/upload/${tipo}`,
                fd,
                { headers: { ...headers, "Content-Type": "multipart/form-data" } }
              );
            } catch (uploadErr) {
              console.error(`Error subiendo ${tipo}:`, uploadErr);
              toast.error(`Error al cargar ${tipo}: ${uploadErr.response?.data?.detail || "Error"}`);
            }
          }
        }
      }
      
      toast.success("Registro creado exitosamente");
      setDialogOpen(false);
      setFormData({});
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear registro");
    }
  };

  const handleUpdate = async () => {
    // Verificar si hay ingreso bruto vacío
    if (tieneIngresoBrutoVacio()) {
      setPendingSaveAction('update');
      setConfirmIngresoBrutoOpen(true);
      return;
    }
    
    try {
      const endpoint = getEndpoint(submodulo);
      const dataToSend = { ...formData };
      delete dataToSend.archivo_libro_diario;
      delete dataToSend.archivo_declaracion_iva;
      delete dataToSend.archivo_otros;
      delete dataToSend.uploading_libro_diario;
      delete dataToSend.uploading_declaracion_iva;
      delete dataToSend.uploading_otros;
      
      await axios.put(`${API}/liquidaciones/${endpoint}/${editingItem.id}`, dataToSend, { headers });
      toast.success("Registro actualizado exitosamente");
      setDialogOpen(false);
      setEditingItem(null);
      setFormData({});
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar registro");
    }
  };

  const handleDelete = async (id) => {
    // Para datos-declaracion, cambiar estatus a "Anulada" en lugar de eliminar
    if (submodulo === "datos-declaracion") {
      if (!window.confirm("¿Está seguro de anular este registro? El registro permanecerá en la bandeja con estatus 'Anulada'.")) return;
      try {
        await axios.put(`${API}/liquidaciones/datos-declaracion/${id}`, { estatus: "Anulada" }, { headers });
        toast.success("Registro anulado exitosamente");
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || "Error al anular registro");
      }
    } else {
      // Para otros submódulos, eliminar normalmente
      if (!window.confirm("¿Está seguro de eliminar este registro?")) return;
      try {
        const endpoint = getEndpoint(submodulo);
        await axios.delete(`${API}/liquidaciones/${endpoint}/${id}`, { headers });
        toast.success("Registro eliminado exitosamente");
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || "Error al eliminar registro");
      }
    }
  };

  // Función para imprimir planilla de liquidación
  const handlePrintDeclaracion = async (item) => {
    // Obtener configuración para logos y nombres
    let configData = {};
    try {
      const response = await axios.get(`${API}/definiciones/configuracion/actual`, { headers });
      configData = response.data;
    } catch (error) {
      console.error("Error obteniendo configuración:", error);
    }
    
    const printWindow = window.open('', '_blank');
    
    // Calcular subtotal de líneas de impuestos
    const subtotalLineas = (item.lineas_impuestos || []).reduce((acc, linea) => acc + (linea.sub_total || 0), 0);
    
    // Generar datos para el QR (información básica de la planilla)
    const qrData = JSON.stringify({
      planilla: item.nro_planilla,
      rif: item.rif,
      fecha: item.fecha,
      total: item.total,
      estatus: item.estatus_pago
    });
    
    // Usar API de QR code para generar la imagen
    const qrImageUrl = `https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(qrData)}`;
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Planilla de Declaración - ${item.nro_planilla}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, sans-serif; padding: 15px; font-size: 11px; }
          
          /* Header con logos */
          .header-container { 
            display: flex; 
            justify-content: space-between; 
            align-items: center; 
            margin-bottom: 15px;
            border-bottom: 2px solid #333;
            padding-bottom: 15px;
          }
          .logo-container { width: 80px; height: 80px; }
          .logo-container img { max-width: 100%; max-height: 100%; object-fit: contain; }
          .header-text { flex: 1; text-align: center; padding: 0 15px; }
          .header-text h1 { font-size: 12px; font-weight: bold; margin-bottom: 3px; }
          .header-text h2 { font-size: 11px; font-weight: bold; margin-bottom: 2px; }
          .header-text h3 { font-size: 10px; margin-bottom: 2px; }
          .header-text h4 { font-size: 13px; font-weight: bold; margin-top: 5px; text-decoration: underline; }
          
          .info-section { margin-bottom: 12px; }
          .info-section h3 { 
            background: #f0f0f0; 
            padding: 6px 8px; 
            margin-bottom: 8px; 
            font-size: 11px; 
            border-left: 3px solid #333; 
          }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 5px; padding: 0 8px; }
          .info-grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 5px; padding: 0 8px; }
          .info-item { display: flex; font-size: 10px; }
          .info-label { font-weight: bold; min-width: 130px; color: #444; }
          .info-value { color: #000; }
          
          table { width: 100%; border-collapse: collapse; margin: 10px 0; font-size: 10px; }
          th, td { border: 1px solid #999; padding: 5px 6px; text-align: left; }
          th { background: #e8e8e8; font-weight: bold; font-size: 9px; }
          .text-right { text-align: right; }
          
          .totals-section { margin-top: 15px; }
          .totals-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
          .total-item { display: flex; justify-content: space-between; padding: 4px 8px; border-bottom: 1px solid #ddd; font-size: 10px; }
          .total-final { 
            background: #333; 
            color: white; 
            padding: 10px 12px; 
            font-size: 12px; 
            font-weight: bold; 
            display: flex; 
            justify-content: space-between; 
            margin-top: 8px; 
          }
          
          .qr-section {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-top: 20px;
            padding-top: 15px;
            border-top: 1px solid #ccc;
          }
          .qr-container { text-align: center; }
          .qr-container img { width: 80px; height: 80px; }
          .qr-container p { font-size: 8px; color: #666; margin-top: 3px; }
          
          .signature-area { display: flex; justify-content: space-around; flex: 1; }
          .signature-box { text-align: center; width: 180px; }
          .signature-line { border-top: 1px solid #333; margin-top: 45px; padding-top: 4px; font-size: 9px; }
          
          .nota-legal {
            margin-top: 20px;
            padding: 10px;
            background: #f9f9f9;
            border: 1px solid #ddd;
            font-size: 8px;
            text-align: justify;
            line-height: 1.4;
          }
          .nota-legal strong { font-size: 9px; }
          
          .footer { 
            margin-top: 15px; 
            text-align: center; 
            font-size: 8px; 
            color: #777; 
            border-top: 1px solid #ccc; 
            padding-top: 8px; 
          }
          
          @media print { 
            body { padding: 10px; } 
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <!-- Encabezado con logos -->
        <div class="header-container">
          <div class="logo-container">
            ${configData.logo_encabezado_izquierdo ? `<img src="${configData.logo_encabezado_izquierdo}" alt="Escudo" />` : '<div style="width:80px;height:80px;border:1px dashed #ccc;display:flex;align-items:center;justify-content:center;font-size:8px;color:#999;">Escudo</div>'}
          </div>
          <div class="header-text">
            <h1>REPÚBLICA BOLIVARIANA DE VENEZUELA</h1>
            <h2>${configData.nombre_alcaldia || 'ALCALDÍA DEL MUNICIPIO'}</h2>
            <h3>${configData.nombre_superintendencia || 'SUPERINTENDENCIA DE ADMINISTRACIÓN TRIBUTARIA'}</h3>
            <h4>PLANILLA DE DECLARACIÓN</h4>
          </div>
          <div class="logo-container">
            ${configData.logo_encabezado_derecho ? `<img src="${configData.logo_encabezado_derecho}" alt="Logo Institucional" />` : '<div style="width:80px;height:80px;border:1px dashed #ccc;display:flex;align-items:center;justify-content:center;font-size:8px;color:#999;">Logo</div>'}
          </div>
        </div>
        
        <div class="info-section">
          <h3>DATOS DE LA PLANILLA</h3>
          <div class="info-grid-3">
            <div class="info-item">
              <span class="info-label">Nro. Planilla:</span>
              <span class="info-value"><strong>${item.nro_planilla || 'N/A'}</strong></span>
            </div>
            <div class="info-item">
              <span class="info-label">Fecha:</span>
              <span class="info-value">${item.fecha || 'N/A'}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Mes/Año:</span>
              <span class="info-value">${item.mes_anio_declaracion || 'N/A'}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Estatus:</span>
              <span class="info-value">${item.estatus || 'N/A'}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Oficina:</span>
              <span class="info-value">${item.oficina_nombre || 'Hacienda'}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Estatus de Pago:</span>
              <span class="info-value"><strong>${item.estatus_pago || 'Sin pagar'}</strong></span>
            </div>
          </div>
        </div>
        
        <div class="info-section">
          <h3>DATOS DEL CONTRIBUYENTE</h3>
          <div class="info-grid">
            <div class="info-item">
              <span class="info-label">Clase:</span>
              <span class="info-value">${item.clase_contribuyente || 'N/A'}</span>
            </div>
            <div class="info-item">
              <span class="info-label">RIF:</span>
              <span class="info-value"><strong>${item.rif || 'N/A'}</strong></span>
            </div>
            <div class="info-item">
              <span class="info-label">Nombre/Razón Social:</span>
              <span class="info-value">${item.nombre_razon_social || item.contribuyente_nombre || 'N/A'}</span>
            </div>
            ${item.lae ? `<div class="info-item"><span class="info-label">LAE:</span><span class="info-value">${item.lae}</span></div>` : ''}
            ${item.codigo_catastral ? `<div class="info-item"><span class="info-label">Código Catastral:</span><span class="info-value">${item.codigo_catastral}</span></div>` : ''}
          </div>
        </div>
        
        <div class="info-section">
          <h3>DETALLE DE IMPUESTOS/TASAS</h3>
          <table>
            <thead>
              <tr>
                <th style="width: 30px">#</th>
                <th>CONCEPTO</th>
                <th class="text-right" style="width: 100px">INGRESO BRUTO</th>
                <th class="text-right" style="width: 80px">IMPORTE</th>
                <th class="text-right" style="width: 100px">SUB TOTAL</th>
              </tr>
            </thead>
            <tbody>
              ${(item.lineas_impuestos || []).map((linea, idx) => `
                <tr>
                  <td>${idx + 1}</td>
                  <td>${linea.impuesto_tasa || linea.tipo_impuesto_nombre || 'N/A'}</td>
                  <td class="text-right">${(linea.ingreso_bruto || 0).toLocaleString('es-VE', { minimumFractionDigits: 2 })}</td>
                  <td class="text-right">${(linea.importe || 0).toLocaleString('es-VE', { minimumFractionDigits: 2 })}</td>
                  <td class="text-right">${(linea.sub_total || 0).toLocaleString('es-VE', { minimumFractionDigits: 2 })}</td>
                </tr>
              `).join('')}
              ${(item.lineas_impuestos || []).length === 0 ? '<tr><td colspan="5" style="text-align: center; color: #999;">Sin líneas de impuestos</td></tr>' : ''}
            </tbody>
            <tfoot>
              <tr style="background: #f0f0f0; font-weight: bold;">
                <td colspan="4" class="text-right">SUBTOTAL:</td>
                <td class="text-right">Bs. ${subtotalLineas.toLocaleString('es-VE', { minimumFractionDigits: 2 })}</td>
              </tr>
            </tfoot>
          </table>
        </div>
        
        <div class="totals-section">
          <h3>RESUMEN DE LIQUIDACIÓN</h3>
          <div class="totals-grid">
            <div>
              <div class="total-item">
                <span>Subtotal Impuestos:</span>
                <span>Bs. ${subtotalLineas.toLocaleString('es-VE', { minimumFractionDigits: 2 })}</span>
              </div>
              <div class="total-item">
                <span>Descuento:</span>
                <span>- Bs. ${(item.descuento || 0).toLocaleString('es-VE', { minimumFractionDigits: 2 })}</span>
              </div>
              <div class="total-item">
                <span>Reintegro:</span>
                <span>- Bs. ${(item.reintegro || 0).toLocaleString('es-VE', { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
            <div>
              <div class="total-item">
                <span>Retención:</span>
                <span>- Bs. ${(item.retencion || 0).toLocaleString('es-VE', { minimumFractionDigits: 2 })}</span>
              </div>
              <div class="total-item">
                <span>Multa:</span>
                <span>+ Bs. ${(item.multa || 0).toLocaleString('es-VE', { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
          </div>
          <div class="total-final">
            <span>TOTAL A PAGAR:</span>
            <span>Bs. ${(item.total || 0).toLocaleString('es-VE', { minimumFractionDigits: 2 })}</span>
          </div>
          ${item.divisas || item.divisas_euro ? `
            <div style="margin-top: 8px; padding: 8px; background: #f9f9f9; border: 1px solid #ddd; font-size: 10px;">
              <strong>Equivalente en Divisas:</strong>
              ${item.divisas ? `USD: $ ${(item.divisas || 0).toLocaleString('es-VE', { minimumFractionDigits: 2 })}` : ''}
              ${item.divisas && item.divisas_euro ? ' | ' : ''}
              ${item.divisas_euro ? `EUR: € ${(item.divisas_euro || 0).toLocaleString('es-VE', { minimumFractionDigits: 2 })}` : ''}
            </div>
          ` : ''}
        </div>
        
        <!-- Sección de QR y Firmas -->
        <div class="qr-section">
          <div class="qr-container">
            <img src="${qrImageUrl}" alt="Código QR" />
            <p>Código de verificación</p>
          </div>
          <div class="signature-area">
            <div class="signature-box">
              <div class="signature-line">Firma del Contribuyente</div>
            </div>
            <div class="signature-box">
              <div class="signature-line">Firma y Sello del Funcionario</div>
            </div>
          </div>
        </div>
        
        <!-- Nota Legal -->
        <div class="nota-legal">
          <strong>Nota:</strong> Todos los montos expresados en este documento son calculados de acuerdo a lo establecido en la Ley Orgánica de Coordinación y Armonización de las Potestades Tributarias de los Estados y Municipios (LOCAPTEM), la Resolución N° 011-2023 publicada en gaceta oficial N° 6.783 de fecha 29 de diciembre de 2023 y las ordenanzas municipales correspondientes en materia tributaria.
        </div>
        
        <div class="footer">
          <p>Documento generado el ${new Date().toLocaleString('es-VE')} | Sistema SIRET - Sistema de Recaudación Tributaria Municipal</p>
        </div>
      </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    
    // Esperar a que cargue el contenido y luego imprimir
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  const getEndpoint = (sub) => {
    const endpoints = {
      "datos-declaracion": "datos-declaracion",
      "documentos-soporte": "documentos-soporte"
    };
    return endpoints[sub];
  };

  const openCreateDialog = async () => {
    setEditingItem(null);
    const today = new Date().toISOString().split('T')[0];
    const defaultValues = { 
      fecha: today,
      reintegro: 0,
      retencion: 0,
      estatus_pago: "Sin pagar"
    };
    if (submodulo === "datos-declaracion") {
      defaultValues.oficina = "Hacienda";
    }
    
    // Portal: auto-buscar y llenar TODOS los datos del contribuyente
    if (isPortal && user) {
      const clase = user.contribuyente_tipo === "con-lae" ? "Contribuyente con LAE" : "Contribuyente sin LAE";
      defaultValues.clase_contribuyente = clase;
      defaultValues.con_actividad_economica = true;

      try {
        const claseParam = user.contribuyente_tipo === "con-lae" ? "con-lae" : "sin-lae";
        const res = await axios.get(`${API}/contribuyentes/buscar?clase=${claseParam}`, { headers });
        const lista = res.data;
        setContribuyentes(lista);

        // Buscar contribuyente que coincida con el RIF del usuario
        const userRif = user.contribuyente_rif || "";
        const matched = lista.find(c => `${c.tipo_rif}-${c.rif}` === userRif);

        if (matched) {
          defaultValues.rif = `${matched.tipo_rif}-${matched.rif}`;
          defaultValues.contribuyente_id = matched.id;
          defaultValues.contribuyente_nombre = matched.nombre;
          defaultValues.nombre_razon_social = matched.nombre;

          if (clase === "Contribuyente con LAE") {
            defaultValues.lae = matched.numero_lae || "";
            defaultValues.codigo_catastral = matched.codigo_catastral || "";

            // Calcular mes de pago, descuento y multa
            const calculo = await calcularMesPagoContribuyente(matched.id);
            if (calculo) {
              defaultValues.mes_anio_declaracion = calculo.proximo_mes_anio;
              if (calculo.meses_pendientes_detalle?.length > 0) {
                const primerMes = calculo.meses_pendientes_detalle[0];
                defaultValues.mes_seleccionado_tipo = primerMes.tipo;
                defaultValues.mes_seleccionado_porcentaje = primerMes.porcentaje;
              }
            }

            // Cargar líneas de actividades económicas
            if (matched.actividades_economicas?.length > 0) {
              const nuevasLineas = matched.actividades_economicas.map(actividadId => {
                const actividad = actividadesEconomicas.find(a => a.id === actividadId);
                if (!actividad) return null;
                const nombreCompleto = `ACTIVIDAD ECONOMICA ${actividad.codigo || ""} - ${actividad.nombre || ""}`.trim();
                let porcentajeDescuento = 0;
                let porcentajeMulta = 0;
                if (defaultValues.mes_seleccionado_tipo === "descuento") {
                  porcentajeDescuento = defaultValues.mes_seleccionado_porcentaje || 0;
                } else if (defaultValues.mes_seleccionado_tipo === "multa") {
                  porcentajeMulta = defaultValues.mes_seleccionado_porcentaje || 0;
                }
                return {
                  tipo_impuesto_id: null,
                  impuesto_tasa: nombreCompleto,
                  tipo_impuesto_nombre: nombreCompleto,
                  codigo_actividad: actividad.codigo || "",
                  actividad_economica_id: actividadId,
                  minimo_tributable: parseFloat(actividad.minimo_tributable) || 0,
                  ingreso_bruto: null,
                  importe: parseFloat(actividad.alicuota) || 0,
                  sub_total: 0,
                  descuento_linea: 0,
                  porcentaje_descuento: porcentajeDescuento,
                  porcentaje_multa: porcentajeMulta,
                  multa_linea: 0
                };
              }).filter(Boolean);
              defaultValues.lineas_impuestos = nuevasLineas;
            }
          } else {
            defaultValues.lae = "No Aplica";
            const now = new Date();
            defaultValues.mes_anio_declaracion = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
            defaultValues.multa = 0;
          }
        } else {
          // Si no se encuentra el contribuyente, al menos poner el RIF
          defaultValues.rif = userRif;
          defaultValues.nombre_razon_social = user.nombre || "";
        }
      } catch (error) {
        console.error("Error auto-fill portal:", error);
        defaultValues.rif = user.contribuyente_rif || "";
        defaultValues.nombre_razon_social = user.nombre || "";
      }
    }

    setFormData(defaultValues);
    setDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    // Set flag to prevent useEffect from clearing RIF data during initial edit load
    setInitialEditLoad(true);
    setFormData(item);
    // Load contribuyentes for the selected clase
    if (item.clase_contribuyente) {
      fetchContribuyentes(item.clase_contribuyente);
    }
    setDialogOpen(true);
  };

  // Filter data
  const filteredData = data.filter(item => {
    // Para datos-declaracion usar filtros múltiples
    if (submodulo === "datos-declaracion") {
      const matchNroPlanilla = !filters.nro_planilla || 
        (item.nro_planilla || "").toLowerCase().includes(filters.nro_planilla.toLowerCase());
      const matchClase = !filters.clase_contribuyente || 
        (item.clase_contribuyente || "").toLowerCase().includes(filters.clase_contribuyente.toLowerCase());
      const matchRif = !filters.rif || 
        (item.rif || "").toLowerCase().includes(filters.rif.toLowerCase());
      const matchNombre = !filters.nombre_razon_social || 
        (item.nombre_razon_social || "").toLowerCase().includes(filters.nombre_razon_social.toLowerCase());
      const matchLae = !filters.lae || 
        (item.lae || "").toLowerCase().includes(filters.lae.toLowerCase());
      const matchCodCatastral = !filters.codigo_catastral || 
        (item.codigo_catastral || "").toLowerCase().includes(filters.codigo_catastral.toLowerCase());
      const matchFecha = !filters.fecha || 
        (item.fecha || "").includes(filters.fecha);
      const matchMesAnio = !filters.mes_anio_declaracion || 
        (item.mes_anio_declaracion || "").includes(filters.mes_anio_declaracion);
      const matchEstatusPago = !filters.estatus_pago || 
        (item.estatus_pago || "Sin pagar").toLowerCase().includes(filters.estatus_pago.toLowerCase());
      const matchEstatus = !filters.estatus || 
        (item.estatus || "Registrada").toLowerCase().includes(filters.estatus.toLowerCase());
      const matchOficina = !filters.oficina || 
        (item.oficina || "").toLowerCase().includes(filters.oficina.toLowerCase());
      
      return matchNroPlanilla && matchClase && matchRif && matchNombre && 
             matchLae && matchCodCatastral && matchFecha && matchMesAnio && 
             matchEstatusPago && matchEstatus && matchOficina;
    }
    
    // Para otros submódulos usar búsqueda general
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      item.nro_planilla?.toLowerCase().includes(searchLower) ||
      item.rif?.toLowerCase().includes(searchLower) ||
      item.clase_contribuyente?.toLowerCase().includes(searchLower) ||
      item.nombre_razon_social?.toLowerCase().includes(searchLower) ||
      item.lae?.toLowerCase().includes(searchLower) ||
      item.codigo_catastral?.toLowerCase().includes(searchLower) ||
      item.tipo_documento?.toLowerCase().includes(searchLower) ||
      item.codigo?.toLowerCase().includes(searchLower)
    );
  }).filter(item => {
    // Portal: ocultar planillas anuladas
    if (isPortal && (item.estatus === "Anulada")) return false;
    return true;
  }).sort((a, b) => (b.nro_planilla || "").localeCompare(a.nro_planilla || "", undefined, { numeric: true }));

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      nro_planilla: "",
      clase_contribuyente: "",
      rif: "",
      nombre_razon_social: "",
      lae: "",
      codigo_catastral: "",
      fecha: "",
      mes_anio_declaracion: "",
      estatus_pago: "",
      estatus: "",
      oficina: ""
    });
  };

  // Clases de contribuyente options
  const clasesContribuyente = [
    "Contribuyente con LAE",
    "Contribuyente sin LAE"
  ];

  // Render form fields based on submodulo
  const renderFormFields = () => {
    if (submodulo === "datos-declaracion") {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Nro de Planilla</Label>
            {editingItem ? (
              <Input 
                value={formData.nro_planilla || ""} 
                disabled
                className="bg-slate-100"
                data-testid="form-nro-planilla"
              />
            ) : (
              <div className="flex items-center gap-2 h-10 px-3 rounded-md border border-slate-200 bg-slate-50">
                <span className="text-slate-500 text-sm italic">Se genera automáticamente</span>
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label>Clase de Contribuyente</Label>
            {isPortal ? (
              <Input 
                value={formData.clase_contribuyente || ""} 
                disabled
                className="bg-slate-100"
                data-testid="form-clase-contribuyente"
              />
            ) : (
            <Select 
              value={formData.clase_contribuyente || ""} 
              onValueChange={(value) => {
                // Limpiar el cálculo de mes/pago cuando cambia la clase de contribuyente
                setCalculoMesPago(null);
                setFormData({...formData, clase_contribuyente: value, contribuyente_id: null, rif: "", contribuyente_nombre: "", nombre_razon_social: ""});
              }}
            >
              <SelectTrigger data-testid="form-clase-contribuyente">
                <SelectValue placeholder="Seleccione clase" />
              </SelectTrigger>
              <SelectContent>
                {clasesContribuyente.map(clase => (
                  <SelectItem key={clase} value={clase}>{clase}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            )}
          </div>
          <div className="space-y-2">
            <Label>RIF / Contribuyente</Label>
            {isPortal ? (
              <Input 
                value={formData.rif || ""} 
                disabled
                className="bg-slate-100"
                data-testid="form-rif"
              />
            ) : (
            <Popover open={rifPopoverOpen} onOpenChange={setRifPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={rifPopoverOpen}
                  className="w-full justify-between h-10 font-normal"
                  disabled={!formData.clase_contribuyente}
                  data-testid="form-rif"
                >
                  {formData.rif ? (
                    <span className="truncate">
                      {contribuyentes.find(c => c.id === formData.contribuyente_id)?.display_name || 
                       (formData.nombre_razon_social ? `${formData.rif} - ${formData.nombre_razon_social}` : formData.rif)}
                    </span>
                  ) : (
                    <span className="text-slate-500">
                      {formData.clase_contribuyente ? "Seleccione contribuyente..." : "Primero seleccione clase"}
                    </span>
                  )}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[400px] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Buscar contribuyente..." />
                  <CommandList>
                    <CommandEmpty>
                      {loadingContribuyentes ? "Cargando..." : "No se encontraron contribuyentes."}
                    </CommandEmpty>
                    <CommandGroup>
                      {contribuyentes.map((contribuyente) => (
                        <CommandItem
                          key={contribuyente.id}
                          value={contribuyente.display_name}
                          onSelect={async () => {
                            const nuevoFormData = {
                              ...formData, 
                              rif: `${contribuyente.tipo_rif}-${contribuyente.rif}`,
                              contribuyente_id: contribuyente.id,
                              contribuyente_nombre: contribuyente.nombre,
                              nombre_razon_social: contribuyente.nombre
                            };
                            
                            // Establecer LAE según la clase de contribuyente
                            if (formData.clase_contribuyente === "Contribuyente con LAE") {
                              // Guardar LAE del contribuyente si existe
                              nuevoFormData.lae = contribuyente.numero_lae || "";
                              
                              // Calcular mes de pago, descuento y multa
                              const calculo = await calcularMesPagoContribuyente(contribuyente.id);
                              
                              if (calculo) {
                                // Auto-llenar mes/año de declaración con el próximo mes a pagar
                                nuevoFormData.mes_anio_declaracion = calculo.proximo_mes_anio;
                                
                                // Establecer el tipo y porcentaje del primer mes pendiente
                                if (calculo.meses_pendientes_detalle && calculo.meses_pendientes_detalle.length > 0) {
                                  const primerMes = calculo.meses_pendientes_detalle[0];
                                  nuevoFormData.mes_seleccionado_tipo = primerMes.tipo;
                                  nuevoFormData.mes_seleccionado_porcentaje = primerMes.porcentaje;
                                  
                                  // Mostrar información al usuario
                                  if (calculo.cantidad_meses_pendientes > 1) {
                                    toast.info(`El contribuyente tiene ${calculo.cantidad_meses_pendientes} meses pendientes de pago`);
                                  }
                                  
                                  // Mostrar si el primer mes tiene multa o descuento
                                  if (primerMes.tipo === "multa") {
                                    toast.warning(`Mes ${primerMes.mes}: Aplica multa del ${primerMes.porcentaje}%`);
                                  } else {
                                    toast.success(`Mes ${primerMes.mes}: Aplica descuento del ${primerMes.porcentaje}%`);
                                  }
                                }
                              }
                            } else if (formData.clase_contribuyente === "Contribuyente sin LAE") {
                              // Para contribuyentes sin LAE, establecer "No Aplica"
                              nuevoFormData.lae = "No Aplica";
                            }
                            
                            setFormData(nuevoFormData);
                            setRifPopoverOpen(false);
                          }}
                          data-testid={`rif-option-${contribuyente.id}`}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              formData.contribuyente_id === contribuyente.id ? "opacity-100" : "opacity-0"
                            )}
                          />
                          <span className="truncate">{contribuyente.display_name}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            )}
          </div>
          <div className="space-y-2">
            <Label>Fecha</Label>
            <Input 
              type="date"
              value={formData.fecha || ""} 
              onChange={(e) => setFormData({...formData, fecha: e.target.value})}
              disabled={isPortal}
              className={isPortal ? "bg-slate-100" : ""}
              data-testid="form-fecha"
            />
          </div>
          <div className="space-y-2">
            <Label>Mes/Año de Declaración</Label>
            <Input 
              type="month"
              value={formData.mes_anio_declaracion || ""} 
              onChange={(e) => setFormData({...formData, mes_anio_declaracion: e.target.value})}
              disabled={isPortal}
              className={isPortal ? "bg-slate-100" : ""}
              data-testid="form-mes-anio"
            />
          </div>
          
          {/* Panel informativo para Contribuyentes con LAE - Descuento/Multa */}
          {formData.clase_contribuyente === "Contribuyente con LAE" && calculoMesPago && (
            <div className="col-span-2 p-4 rounded-lg border bg-gradient-to-r from-slate-50 to-white" data-testid="panel-calculo-lae">
              <div className="flex items-start gap-4">
                <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-4">
                  {/* Información del período */}
                  <div className="space-y-1">
                    <span className="text-xs text-slate-500 font-medium">Próximo Mes a Pagar</span>
                    <p className="font-semibold text-blue-700" data-testid="proximo-mes-pagar">
                      {calculoMesPago.proximo_mes_anio}
                    </p>
                  </div>
                  
                  {/* Meses pendientes */}
                  <div className="space-y-1">
                    <span className="text-xs text-slate-500 font-medium">Meses Pendientes</span>
                    <p className={`font-semibold ${calculoMesPago.cantidad_meses_pendientes > 1 ? 'text-amber-600' : 'text-green-600'}`} data-testid="meses-pendientes">
                      {calculoMesPago.cantidad_meses_pendientes} mes(es)
                    </p>
                  </div>
                  
                  {/* Resumen de estados */}
                  <div className="space-y-1">
                    <span className="text-xs text-slate-500 font-medium">Resumen</span>
                    <div className="flex gap-2">
                      {calculoMesPago.cantidad_meses_con_multa > 0 && (
                        <Badge className="bg-red-100 text-red-800 border-red-200" data-testid="badge-multa-count">
                          {calculoMesPago.cantidad_meses_con_multa} con multa
                        </Badge>
                      )}
                      {calculoMesPago.cantidad_meses_con_descuento > 0 && (
                        <Badge className="bg-green-100 text-green-800 border-green-200" data-testid="badge-descuento-count">
                          {calculoMesPago.cantidad_meses_con_descuento} con descuento
                        </Badge>
                      )}
                      {calculoMesPago.cantidad_meses_normales > 0 && (
                        <Badge className="bg-blue-100 text-blue-800 border-blue-200" data-testid="badge-normal-count">
                          {calculoMesPago.cantidad_meses_normales} sin ajuste
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  {/* Día actual vs límites */}
                  <div className="space-y-1">
                    <span className="text-xs text-slate-500 font-medium">Día del Mes</span>
                    <p className="text-sm text-slate-700" data-testid="dia-actual-info">
                      Día {calculoMesPago.dia_actual}
                      {calculoMesPago.fecha_inicio_descuento_dia && (
                        <span className="text-green-600 ml-1">(Desc. hasta día {calculoMesPago.fecha_inicio_descuento_dia})</span>
                      )}
                    </p>
                    {calculoMesPago.fecha_multa_config && (
                      <p className="text-xs text-slate-500" data-testid="fecha-multa-info">
                        Multa último mes desde: {calculoMesPago.fecha_multa_config}
                        {calculoMesPago.mes_anterior_con_multa 
                          ? <span className="text-red-600 ml-1">(Activa)</span>
                          : <span className="text-blue-600 ml-1">(Pendiente)</span>
                        }
                      </p>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Mostrar meses pendientes con su estado individual */}
              {calculoMesPago.meses_pendientes_detalle && calculoMesPago.meses_pendientes_detalle.length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-200">
                  <span className="text-xs text-slate-500 font-medium mb-2 block">Seleccione el mes a pagar (debe seguir el orden cronológico):</span>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {calculoMesPago.meses_pendientes_detalle.map((mesDetalle, idx) => {
                      const esProximoAPagar = idx === 0; // Solo el primero (más viejo) se puede seleccionar
                      const esSeleccionado = formData.mes_anio_declaracion === mesDetalle.mes;
                      const tieneMulta = mesDetalle.tipo === "multa";
                      const tieneDescuento = mesDetalle.tipo === "descuento";
                      const esNormal = mesDetalle.tipo === "normal";
                      
                      return (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => {
                            if (esProximoAPagar) {
                              // Actualizar el mes seleccionado y el porcentaje de descuento/multa
                              setFormData({
                                ...formData,
                                mes_anio_declaracion: mesDetalle.mes,
                                mes_seleccionado_tipo: mesDetalle.tipo,
                                mes_seleccionado_porcentaje: mesDetalle.porcentaje
                              });
                              
                              if (tieneMulta) {
                                toast.warning(`Mes ${mesDetalle.mes} con multa del ${mesDetalle.porcentaje}%`);
                              } else if (tieneDescuento) {
                                toast.success(`Mes ${mesDetalle.mes} con descuento del ${mesDetalle.porcentaje}%`);
                              } else {
                                toast.info(`Mes ${mesDetalle.mes} sin ajuste (ni multa ni descuento)`);
                              }
                            } else {
                              toast.error(`Debe pagar primero el mes ${calculoMesPago.meses_pendientes_detalle[0].mes}`);
                            }
                          }}
                          disabled={!esProximoAPagar}
                          className={`
                            px-3 py-2 rounded-lg text-sm font-medium transition-all
                            ${esSeleccionado ? 'ring-2 ring-blue-500 ring-offset-1' : ''}
                            ${esProximoAPagar 
                              ? tieneMulta 
                                ? 'bg-red-100 text-red-800 border border-red-300 hover:bg-red-200 cursor-pointer' 
                                : tieneDescuento
                                  ? 'bg-green-100 text-green-800 border border-green-300 hover:bg-green-200 cursor-pointer'
                                  : 'bg-blue-100 text-blue-800 border border-blue-300 hover:bg-blue-200 cursor-pointer'
                              : 'bg-slate-100 text-slate-400 border border-slate-200 cursor-not-allowed opacity-60'
                            }
                          `}
                          data-testid={`mes-btn-${mesDetalle.mes}`}
                        >
                          <div className="flex flex-col items-center">
                            <span>{mesDetalle.mes}</span>
                            <span className="text-xs mt-0.5">
                              {tieneMulta ? `+${mesDetalle.porcentaje}% multa` : tieneDescuento ? `-${mesDetalle.porcentaje}% desc.` : 'Sin ajuste'}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-slate-400 mt-2 italic">
                    * Solo puede pagar el mes más antiguo. Los demás se desbloquearán después de pagar.
                  </p>
                </div>
              )}
            </div>
          )}
          {formData.clase_contribuyente === "Contribuyente con LAE" && (
            <div className="space-y-2 flex items-center gap-2 pt-6">
              <input 
                type="checkbox"
                id="con_actividad_economica"
                checked={formData.con_actividad_economica || false}
                disabled={isPortal}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  
                  if (isChecked && formData.contribuyente_id) {
                    // Buscar el contribuyente seleccionado para obtener sus actividades económicas
                    const contribuyenteSeleccionado = contribuyentes.find(c => c.id === formData.contribuyente_id);
                    
                    if (contribuyenteSeleccionado && contribuyenteSeleccionado.actividades_economicas?.length > 0) {
                      // Crear líneas de impuestos basadas en las actividades económicas del contribuyente
                      const nuevasLineas = contribuyenteSeleccionado.actividades_economicas.map(actividadId => {
                        const actividad = actividadesEconomicas.find(a => a.id === actividadId);
                        if (actividad) {
                          // Formato: "ACTIVIDAD ECONOMICA [código] - [nombre]"
                          const nombreCompleto = `ACTIVIDAD ECONOMICA ${actividad.codigo || ""} - ${actividad.nombre || ""}`.trim();
                          const minimoTributable = parseFloat(actividad.minimo_tributable) || 0;
                          
                          // Usar el porcentaje del mes seleccionado (desde formData o calculoMesPago)
                          let porcentajeDescuento = 0;
                          let porcentajeMulta = 0;
                          
                          // Prioridad: usar el tipo/porcentaje del mes seleccionado
                          if (formData.mes_seleccionado_tipo === "descuento") {
                            porcentajeDescuento = formData.mes_seleccionado_porcentaje || 0;
                            // Validar solvencias si el botón está activo
                            if (calculoMesPago?.descuento_solvencias?.activo && !calculoMesPago?.descuento_solvencias?.puede_gozar_descuento) {
                              porcentajeDescuento = 0; // No aplica descuento
                            }
                          } else if (formData.mes_seleccionado_tipo === "multa") {
                            porcentajeMulta = formData.mes_seleccionado_porcentaje || 0;
                          }
                          
                          return {
                            tipo_impuesto_id: null,
                            impuesto_tasa: nombreCompleto,
                            tipo_impuesto_nombre: nombreCompleto,
                            codigo_actividad: actividad.codigo || "",
                            actividad_economica_id: actividadId,
                            minimo_tributable: minimoTributable,
                            ingreso_bruto: null, // Dejar vacío para que el usuario ingrese el valor
                            importe: parseFloat(actividad.alicuota) || 0,
                            sub_total: 0,
                            descuento_linea: 0,
                            porcentaje_descuento: porcentajeDescuento,
                            porcentaje_multa: porcentajeMulta,
                            multa_linea: 0
                          };
                        }
                        return null;
                      }).filter(linea => linea !== null);
                      
                      // Agregar las nuevas líneas a las existentes
                      const lineasActuales = formData.lineas_impuestos || [];
                      
                      // Mensaje sobre descuento/multa aplicado
                      let mensajeAdicional = "";
                      if (formData.mes_seleccionado_tipo === "descuento") {
                        mensajeAdicional = ` con ${formData.mes_seleccionado_porcentaje}% de descuento`;
                      } else if (formData.mes_seleccionado_tipo === "multa") {
                        mensajeAdicional = ` con ${formData.mes_seleccionado_porcentaje}% de multa`;
                      }
                      
                      setFormData({
                        ...formData, 
                        con_actividad_economica: isChecked,
                        lineas_impuestos: [...lineasActuales, ...nuevasLineas]
                      });
                      
                      if (nuevasLineas.length > 0) {
                        toast.success(`Se agregaron ${nuevasLineas.length} actividad(es) económica(s)${mensajeAdicional}`);
                        // Mostrar advertencia si no puede gozar del descuento
                        if (calculoMesPago?.descuento_solvencias?.activo && !calculoMesPago?.descuento_solvencias?.puede_gozar_descuento && formData.mes_seleccionado_tipo === "descuento") {
                          const motivo = calculoMesPago.descuento_solvencias.motivo_sin_descuento || "";
                          toast.warning(`El contribuyente no puede gozar del descuento. ${motivo}`, { duration: 8000 });
                        }
                      } else {
                        toast.warning("No se pudieron cargar las actividades económicas. Verifique que existan en el sistema.");
                      }
                    } else {
                      toast.warning("El contribuyente seleccionado no tiene actividades económicas registradas");
                      setFormData({...formData, con_actividad_economica: isChecked});
                    }
                  } else if (!formData.contribuyente_id && isChecked) {
                    toast.warning("Primero debe seleccionar un contribuyente");
                    return; // No marcar el checkbox si no hay contribuyente
                  } else {
                    // Si se deselecciona el checkbox, eliminar las líneas de actividades económicas
                    if (!isChecked) {
                      const lineasActuales = formData.lineas_impuestos || [];
                      const lineasFiltradas = lineasActuales.filter(linea => !linea.actividad_economica_id);
                      const lineasEliminadas = lineasActuales.length - lineasFiltradas.length;
                      
                      // Recalcular descuento total
                      const descuentoTotal = lineasFiltradas.reduce((acc, l) => acc + (l.descuento_linea || 0), 0);
                      
                      setFormData({
                        ...formData, 
                        con_actividad_economica: isChecked,
                        lineas_impuestos: lineasFiltradas,
                        descuento: descuentoTotal
                      });
                      
                      if (lineasEliminadas > 0) {
                        toast.info(`Se eliminaron ${lineasEliminadas} actividad(es) económica(s)`);
                      }
                    } else {
                      setFormData({...formData, con_actividad_economica: isChecked});
                    }
                  }
                }}
                className="h-4 w-4 rounded border-gray-300"
                data-testid="form-con-actividad"
              />
              <Label htmlFor="con_actividad_economica" className="cursor-pointer">¿Con Actividad Económica?</Label>
            </div>
          )}
          <div className="space-y-2">
            <Label>Estatus de Pago</Label>
            <div className="flex items-center gap-2 h-10 px-3 rounded-md border border-slate-200 bg-slate-50">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                formData.estatus_pago === "Pagado" 
                  ? "bg-green-100 text-green-800" 
                  : "bg-amber-100 text-amber-800"
              }`}>
                {formData.estatus_pago || "Sin pagar"}
              </span>
              <span className="text-slate-400 text-xs italic ml-auto">Solo lectura</span>
            </div>
            <p className="text-xs text-slate-500">Este campo se actualiza desde el módulo de Recaudación</p>
          </div>
          <div className="space-y-2">
            <Label>Estatus</Label>
            {isPortal ? (
              <Input 
                value={formData.estatus || "Registrada"} 
                disabled
                className="bg-slate-100"
                data-testid="form-estatus"
              />
            ) : (
            <Select 
              value={formData.estatus || "Registrada"} 
              onValueChange={(value) => setFormData({...formData, estatus: value})}
            >
              <SelectTrigger data-testid="form-estatus">
                <SelectValue placeholder="Seleccione estatus" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Registrada">Registrada</SelectItem>
                <SelectItem value="Anulada">Anulada</SelectItem>
              </SelectContent>
            </Select>
            )}
          </div>
          <div className="space-y-2">
            <Label>Oficina</Label>
            <Input 
              value={editingItem ? (editingItem.oficina_nombre || editingItem.oficina || "Hacienda") : "Hacienda"}
              disabled
              className="bg-slate-100 cursor-not-allowed"
              data-testid="form-oficina"
            />
          </div>
          
          {/* Tipos de Impuestos (oculto para portal) */}
          {!isPortal && (
          <div className="space-y-2 col-span-2">
            <div className="flex items-center justify-between">
              <Label>Tipos de Impuestos</Label>
              <Badge variant="outline" className="bg-cyan-50 text-cyan-700 border-cyan-200">
                {(formData.tipos_impuestos || []).length} seleccionados
              </Badge>
            </div>
            <Popover open={tiposImpuestoOpen} onOpenChange={setTiposImpuestoOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={tiposImpuestoOpen}
                  className="w-full justify-between h-auto min-h-10 py-2"
                  data-testid="tipos-impuestos-trigger"
                >
                  <div className="flex flex-wrap gap-1 flex-1 text-left">
                    {(formData.tipos_impuestos || []).length === 0 ? (
                      <span className="text-slate-500">Seleccione tipos de impuestos...</span>
                    ) : (
                      (formData.tipos_impuestos || []).map(tipoId => {
                        const tipo = tiposImpuesto.find(t => t.id === tipoId);
                        return tipo ? (
                          <Badge 
                            key={tipoId} 
                            variant="secondary" 
                            className="bg-cyan-100 text-cyan-800 hover:bg-cyan-200"
                          >
                            {tipo.nombre}
                            <span
                              role="button"
                              tabIndex={0}
                              className="ml-1 hover:text-cyan-900 cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                const currentLineas = formData.lineas_impuestos || [];
                                const nuevasLineas = currentLineas.filter(l => l.tipo_impuesto_id !== tipoId);
                                const descuentoTotal = nuevasLineas.reduce((acc, linea) => acc + (linea.descuento_linea || 0), 0);
                                setFormData({
                                  ...formData, 
                                  tipos_impuestos: (formData.tipos_impuestos || []).filter(t => t !== tipoId),
                                  lineas_impuestos: nuevasLineas,
                                  descuento: descuentoTotal
                                });
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  const currentLineas = formData.lineas_impuestos || [];
                                  const nuevasLineas = currentLineas.filter(l => l.tipo_impuesto_id !== tipoId);
                                  const descuentoTotal = nuevasLineas.reduce((acc, linea) => acc + (linea.descuento_linea || 0), 0);
                                  setFormData({
                                    ...formData, 
                                    tipos_impuestos: (formData.tipos_impuestos || []).filter(t => t !== tipoId),
                                    lineas_impuestos: nuevasLineas,
                                    descuento: descuentoTotal
                                  });
                                }
                              }}
                            >
                              <X className="h-3 w-3" />
                            </span>
                          </Badge>
                        ) : null;
                      })
                    )}
                  </div>
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0" align="start">
                <div className="p-2 border-b space-y-2">
                  {/* Filtro por Oficina - Fijo en Hacienda */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500 font-medium whitespace-nowrap">Oficina:</span>
                    <input
                      value="Hacienda"
                      disabled
                      className="flex-1 h-8 text-sm border rounded-md px-2 bg-slate-100 cursor-not-allowed"
                      data-testid="filtro-oficina"
                    />
                  </div>
                  {/* Búsqueda */}
                  <div className="flex items-center gap-2 px-2">
                    <Search className="h-4 w-4 text-slate-400" />
                    <Input
                      placeholder="Buscar tipo de impuesto..."
                      value={tiposImpuestoSearch}
                      onChange={(e) => setTiposImpuestoSearch(e.target.value)}
                      className="border-0 p-0 h-8 focus-visible:ring-0 focus-visible:ring-offset-0"
                      data-testid="tipos-impuestos-search"
                    />
                  </div>
                </div>
                <div className="max-h-60 overflow-y-auto p-1">
                  {tiposImpuesto.length === 0 ? (
                    <p className="text-sm text-slate-500 italic text-center py-4">
                      No hay tipos de impuesto disponibles.
                    </p>
                  ) : (
                    tiposImpuesto
                      .filter(tipo => {
                        // Filtro por oficina - solo mostrar los de Hacienda
                        const oficinaHacienda = oficinas.find(o => o.nombre === "Hacienda");
                        if (oficinaHacienda && tipo.oficina_id !== oficinaHacienda.id) {
                          return false;
                        }
                        // Filtro por búsqueda de texto
                        return tipo.nombre.toLowerCase().includes(tiposImpuestoSearch.toLowerCase()) ||
                          (tipo.descripcion && tipo.descripcion.toLowerCase().includes(tiposImpuestoSearch.toLowerCase()));
                      })
                      .map((tipo) => {
                        // Contar cuántas líneas de este tipo ya existen
                        const lineasDeTipo = (formData.lineas_impuestos || []).filter(l => l.tipo_impuesto_id === tipo.id);
                        const cantidadLineas = lineasDeTipo.length;
                        
                        // Función para agregar una línea de este tipo de impuesto
                        const agregarLinea = (e) => {
                          e.stopPropagation();
                          const currentTipos = formData.tipos_impuestos || [];
                          const currentLineas = formData.lineas_impuestos || [];
                          
                          // Buscar el tipo de cálculo para determinar si es "tasa"
                          const tipoCalculo = tiposCalculo.find(tc => tc.id === tipo.tipo_calculo_id);
                          const esTasa = tipoCalculo && tipoCalculo.nombre.toLowerCase().includes('tasa');
                          
                          // Valor de unidad de cuenta de cotización activa
                          const valorUnidadCuenta = cotizacionActiva?.valor_unidad_cuenta || 0;
                          
                          // El importe es directamente el valor de la tasa del tipo de impuesto
                          const importeCalculado = esTasa ? (tipo.tasa || 0) : 0;
                          
                          // El ingreso bruto es la unidad de cuenta activa para el tipo "Tasa"
                          const ingresoBrutoCalculado = esTasa ? valorUnidadCuenta : 0;
                          
                          // El sub total es Ingreso Bruto × Importe
                          const subTotalCalculado = ingresoBrutoCalculado * importeCalculado;
                          
                          // Calcular el descuento de esta línea
                          const descuentoLinea = tipo.descuento ? (subTotalCalculado * (tipo.porcentaje_descuento || 0) / 100) : 0;
                          
                          // Crear la nueva línea con un ID único
                          const nuevaLinea = {
                            id: `${tipo.id}-${Date.now()}`, // ID único para cada línea
                            tipo_impuesto_id: tipo.id,
                            impuesto_tasa: tipo.nombre,
                            ingreso_bruto: ingresoBrutoCalculado,
                            importe: importeCalculado,
                            sub_total: subTotalCalculado,
                            descuento_linea: descuentoLinea,
                            porcentaje_descuento: tipo.descuento ? (tipo.porcentaje_descuento || 0) : 0
                          };
                          
                          // Agregar la nueva línea
                          const nuevasLineas = [...currentLineas, nuevaLinea];
                          const descuentoTotal = nuevasLineas.reduce((acc, linea) => acc + (linea.descuento_linea || 0), 0);
                          
                          // Agregar el tipo a la lista si no está
                          const nuevosTipos = currentTipos.includes(tipo.id) ? currentTipos : [...currentTipos, tipo.id];
                          
                          setFormData({
                            ...formData, 
                            tipos_impuestos: nuevosTipos,
                            lineas_impuestos: nuevasLineas,
                            descuento: descuentoTotal
                          });
                        };
                        
                        return (
                          <div
                            key={tipo.id}
                            className={`flex items-center gap-2 px-2 py-2 rounded transition-colors ${
                              cantidadLineas > 0 ? 'bg-cyan-50' : 'hover:bg-slate-100'
                            }`}
                            data-testid={`tipo-impuesto-option-${tipo.id}`}
                          >
                            <div className={`flex items-center justify-center w-5 h-5 rounded border text-xs font-medium ${
                              cantidadLineas > 0 ? 'bg-cyan-600 border-cyan-600 text-white' : 'border-slate-300 text-slate-400'
                            }`}>
                              {cantidadLineas > 0 ? cantidadLineas : '0'}
                            </div>
                            <div className="flex-1">
                              <span className="font-medium text-sm">{tipo.nombre}</span>
                              {tipo.descripcion && (
                                <span className="text-slate-500 text-xs ml-2">- {tipo.descripcion}</span>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={agregarLinea}
                              className="flex items-center justify-center w-7 h-7 rounded-full bg-green-500 hover:bg-green-600 text-white transition-colors"
                              title="Agregar línea"
                              data-testid={`agregar-linea-${tipo.id}`}
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                          </div>
                        );
                      })
                  )}
                  {tiposImpuesto.length > 0 && 
                   tiposImpuesto.filter(tipo => {
                     // Filtro por oficina - solo mostrar los de Hacienda
                     const oficinaHacienda = oficinas.find(o => o.nombre === "Hacienda");
                     if (oficinaHacienda && tipo.oficina_id !== oficinaHacienda.id) {
                       return false;
                     }
                     // Filtro por búsqueda de texto
                     return tipo.nombre.toLowerCase().includes(tiposImpuestoSearch.toLowerCase()) ||
                       (tipo.descripcion && tipo.descripcion.toLowerCase().includes(tiposImpuestoSearch.toLowerCase()));
                   }).length === 0 && (
                    <p className="text-sm text-slate-500 italic text-center py-4">
                      No se encontraron resultados {tiposImpuestoSearch ? `para "${tiposImpuestoSearch}"` : "para la oficina Hacienda"}
                    </p>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          </div>
          )}
          
          {/* Tabla de Impuestos/Tasas */}
          <div className="space-y-2 col-span-2">
            <Label className="text-base font-semibold">Líneas de Impuestos/Tasas</Label>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-slate-700 w-10">#</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-700">IMPUESTOS/TASAS</th>
                    <th className="px-3 py-2 text-right font-medium text-slate-700 w-32">INGRESO BRUTO</th>
                    <th className="px-3 py-2 text-right font-medium text-slate-700 w-28">IMPORTE</th>
                    <th className="px-3 py-2 text-right font-medium text-slate-700 w-32">SUB TOTAL</th>
                    {/* Columnas de Descuento/Multa solo para Contribuyentes con LAE */}
                    {formData.clase_contribuyente === "Contribuyente con LAE" && formData.mes_seleccionado_tipo && formData.mes_seleccionado_tipo !== 'normal' && (
                      <th className={`px-3 py-2 text-right font-medium w-32 ${formData.mes_seleccionado_tipo === 'descuento' ? 'text-green-700' : 'text-red-700'}`}>
                        {formData.mes_seleccionado_tipo === 'descuento' ? `DESC. (${formData.mes_seleccionado_porcentaje}%)` : `MULTA (${formData.mes_seleccionado_porcentaje}%)`}
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {(formData.lineas_impuestos || []).length === 0 ? (
                    <tr>
                      <td colSpan={formData.clase_contribuyente === "Contribuyente con LAE" && formData.mes_seleccionado_tipo && formData.mes_seleccionado_tipo !== 'normal' ? 6 : 5} className="px-3 py-4 text-center text-slate-400 italic">
                        Seleccione tipos de impuestos para agregar líneas automáticamente.
                      </td>
                    </tr>
                  ) : (
                    (formData.lineas_impuestos || []).map((linea, index) => (
                      <tr key={index} className="border-t hover:bg-slate-50">
                        <td className="px-3 py-2 text-slate-500">{index + 1}</td>
                        <td className="px-3 py-2">
                          <Input
                            value={linea.impuesto_tasa || ""}
                            onChange={(e) => {
                              const lineas = [...(formData.lineas_impuestos || [])];
                              lineas[index] = { ...lineas[index], impuesto_tasa: e.target.value };
                              setFormData({ ...formData, lineas_impuestos: lineas });
                            }}
                            placeholder="Ej: PUBLICIDAD 2DO. TRIMESTRE"
                            className="h-8 text-sm"
                            data-testid={`linea-impuesto-${index}`}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <Input
                            type="number"
                            step="0.01"
                            value={linea.ingreso_bruto !== null && linea.ingreso_bruto !== undefined ? linea.ingreso_bruto : ""}
                            onChange={(e) => {
                              const lineas = [...(formData.lineas_impuestos || [])];
                              const valorInput = e.target.value;
                              // Si está vacío, guardar null para validación posterior
                              const nuevoIngresoBruto = valorInput === "" ? null : parseFloat(valorInput);
                              const importe = lineas[index].importe || 0;
                              const porcentajeDescuento = lineas[index].porcentaje_descuento || 0;
                              const porcentajeMulta = lineas[index].porcentaje_multa || 0;
                              const esActividadEconomica = lineas[index].actividad_economica_id;
                              
                              // Recalcular Sub Total según tipo de cálculo
                              // Si es Actividad Económica: Sub Total = Ingreso Bruto × (Importe / 100)
                              // Si no: Sub Total = Ingreso Bruto × Importe
                              const ingresoBrutoCalculo = nuevoIngresoBruto || 0;
                              let nuevoSubTotal = esActividadEconomica 
                                ? ingresoBrutoCalculo * (importe / 100)
                                : ingresoBrutoCalculo * importe;
                              
                              // Aplicar mínimo tributable para actividades económicas
                              // Fórmula: Mínimo Tributable Real = minimo_tributable × valor_unidad_cuenta
                              if (esActividadEconomica) {
                                const minimoTributable = lineas[index].minimo_tributable || 0;
                                const valorUnidadCuenta = cotizacionActiva?.valor_unidad_cuenta || 0;
                                const minimoTributableReal = minimoTributable * valorUnidadCuenta;
                                
                                if (nuevoSubTotal < minimoTributableReal) {
                                  nuevoSubTotal = minimoTributableReal;
                                }
                              }
                              
                              // Recalcular descuento de la línea
                              const nuevoDescuentoLinea = nuevoSubTotal * (porcentajeDescuento / 100);
                              
                              // Recalcular multa de la línea
                              const nuevaMultaLinea = nuevoSubTotal * (porcentajeMulta / 100);
                              
                              lineas[index] = { 
                                ...lineas[index], 
                                ingreso_bruto: nuevoIngresoBruto,
                                sub_total: nuevoSubTotal,
                                descuento_linea: nuevoDescuentoLinea,
                                multa_linea: nuevaMultaLinea
                              };
                              
                              // Recalcular totales
                              const descuentoTotal = lineas.reduce((acc, l) => acc + (l.descuento_linea || 0), 0);
                              const multaTotal = lineas.reduce((acc, l) => acc + (l.multa_linea || 0), 0);
                              setFormData({ ...formData, lineas_impuestos: lineas, descuento: descuentoTotal, multa: multaTotal });
                            }}
                            placeholder="0.00"
                            className="h-8 text-sm text-right"
                            data-testid={`linea-ingreso-${index}`}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <Input
                            type="number"
                            step="0.01"
                            value={linea.importe || ""}
                            onChange={(e) => {
                              const lineas = [...(formData.lineas_impuestos || [])];
                              const nuevoImporte = parseFloat(e.target.value) || 0;
                              const ingresoBruto = lineas[index].ingreso_bruto || 0;
                              const porcentajeDescuento = lineas[index].porcentaje_descuento || 0;
                              const porcentajeMulta = lineas[index].porcentaje_multa || 0;
                              const esActividadEconomica = lineas[index].actividad_economica_id;
                              
                              // Recalcular Sub Total según tipo de cálculo
                              // Si es Actividad Económica: Sub Total = Ingreso Bruto × (Importe / 100)
                              // Si no: Sub Total = Ingreso Bruto × Importe
                              let nuevoSubTotal = esActividadEconomica 
                                ? ingresoBruto * (nuevoImporte / 100)
                                : ingresoBruto * nuevoImporte;
                              
                              // Aplicar mínimo tributable para actividades económicas
                              // Fórmula: Mínimo Tributable Real = minimo_tributable × valor_unidad_cuenta
                              if (esActividadEconomica) {
                                const minimoTributable = lineas[index].minimo_tributable || 0;
                                const valorUnidadCuenta = cotizacionActiva?.valor_unidad_cuenta || 0;
                                const minimoTributableReal = minimoTributable * valorUnidadCuenta;
                                
                                if (nuevoSubTotal < minimoTributableReal) {
                                  nuevoSubTotal = minimoTributableReal;
                                }
                              }
                              
                              // Recalcular descuento de la línea
                              const nuevoDescuentoLinea = nuevoSubTotal * (porcentajeDescuento / 100);
                              
                              // Recalcular multa de la línea
                              const nuevaMultaLinea = nuevoSubTotal * (porcentajeMulta / 100);
                              
                              lineas[index] = { 
                                ...lineas[index], 
                                importe: nuevoImporte,
                                sub_total: nuevoSubTotal,
                                descuento_linea: nuevoDescuentoLinea,
                                multa_linea: nuevaMultaLinea
                              };
                              
                              // Recalcular totales
                              const descuentoTotal = lineas.reduce((acc, l) => acc + (l.descuento_linea || 0), 0);
                              const multaTotal = lineas.reduce((acc, l) => acc + (l.multa_linea || 0), 0);
                              setFormData({ ...formData, lineas_impuestos: lineas, descuento: descuentoTotal, multa: multaTotal });
                            }}
                            placeholder="0.00"
                            className="h-8 text-sm text-right"
                            data-testid={`linea-importe-${index}`}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <Input
                            type="number"
                            step="0.01"
                            value={linea.sub_total || ""}
                            onChange={(e) => {
                              const lineas = [...(formData.lineas_impuestos || [])];
                              const nuevoSubTotal = parseFloat(e.target.value) || 0;
                              const porcentajeDescuento = lineas[index].porcentaje_descuento || 0;
                              const porcentajeMulta = lineas[index].porcentaje_multa || 0;
                              // Recalcular descuento de la línea
                              const nuevoDescuentoLinea = nuevoSubTotal * (porcentajeDescuento / 100);
                              // Recalcular multa de la línea
                              const nuevaMultaLinea = nuevoSubTotal * (porcentajeMulta / 100);
                              
                              lineas[index] = { 
                                ...lineas[index], 
                                sub_total: nuevoSubTotal,
                                descuento_linea: nuevoDescuentoLinea,
                                multa_linea: nuevaMultaLinea
                              };
                              
                              // Recalcular totales
                              const descuentoTotal = lineas.reduce((acc, l) => acc + (l.descuento_linea || 0), 0);
                              const multaTotal = lineas.reduce((acc, l) => acc + (l.multa_linea || 0), 0);
                              setFormData({ ...formData, lineas_impuestos: lineas, descuento: descuentoTotal, multa: multaTotal });
                            }}
                            placeholder="0.00"
                            className="h-8 text-sm text-right"
                            data-testid={`linea-subtotal-${index}`}
                          />
                        </td>
                        {/* Columna de Descuento/Multa solo para Contribuyentes con LAE */}
                        {formData.clase_contribuyente === "Contribuyente con LAE" && formData.mes_seleccionado_tipo && formData.mes_seleccionado_tipo !== 'normal' && (
                          <td className="px-3 py-2">
                            <div className={`px-2 py-1 rounded text-right text-sm font-medium ${
                              formData.mes_seleccionado_tipo === 'descuento' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                            }`}>
                              {formData.mes_seleccionado_tipo === 'descuento' 
                                ? `-${(linea.descuento_linea || 0).toFixed(2)}`
                                : `+${(linea.multa_linea || 0).toFixed(2)}`
                              }
                            </div>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Descuento (Bs.) {formData.clase_contribuyente === "Contribuyente con LAE" && formData.mes_seleccionado_tipo === "descuento" ? (
              <span className="text-xs text-green-600 font-normal">(Calculado - {formData.mes_seleccionado_porcentaje}%)</span>
            ) : (
              <span className="text-xs text-slate-500 font-normal">(Calculado automáticamente)</span>
            )}</Label>
            <Input 
              type="number"
              step="0.01"
              value={formData.descuento !== undefined ? formData.descuento : 0} 
              readOnly
              className={formData.clase_contribuyente === "Contribuyente con LAE" && formData.mes_seleccionado_tipo === "descuento" ? "bg-green-50 border-green-300" : "bg-slate-50"}
              placeholder="0.00"
              data-testid="form-descuento"
            />
          </div>
          <div className="space-y-2">
            <Label>Reintegro (Bs.)</Label>
            <Input 
              type="number"
              step="0.01"
              value={formData.reintegro !== null && formData.reintegro !== undefined ? formData.reintegro : ""} 
              onChange={(e) => setFormData({...formData, reintegro: e.target.value === "" ? null : parseFloat(e.target.value)})}
              onBlur={(e) => {
                if (e.target.value === "" || formData.reintegro === null) {
                  toast.warning("El valor mínimo para Reintegro debe ser 0");
                  setFormData({...formData, reintegro: 0});
                }
              }}
              placeholder="0.00"
              disabled={isPortal}
              className={isPortal ? "bg-slate-100" : ""}
              data-testid="form-reintegro"
            />
          </div>
          <div className="space-y-2">
            <Label>Retención (Bs.)</Label>
            <Input 
              type="number"
              step="0.01"
              value={formData.retencion !== null && formData.retencion !== undefined ? formData.retencion : ""} 
              onChange={(e) => setFormData({...formData, retencion: e.target.value === "" ? null : parseFloat(e.target.value)})}
              onBlur={(e) => {
                if (e.target.value === "" || formData.retencion === null) {
                  toast.warning("El valor mínimo para Retención debe ser 0");
                  setFormData({...formData, retencion: 0});
                }
              }}
              placeholder="0.00"
              disabled={isPortal}
              className={isPortal ? "bg-slate-100" : ""}
              data-testid="form-retencion"
            />
          </div>
          <div className="space-y-2">
            <Label>Multa (Bs.) {formData.clase_contribuyente === "Contribuyente con LAE" && formData.mes_seleccionado_tipo === "multa" && (
              <span className="text-xs text-red-500 font-normal ml-1">(Calculado automáticamente - {formData.mes_seleccionado_porcentaje}%)</span>
            )}</Label>
            <Input 
              type="number"
              step="0.01"
              value={formData.multa !== null && formData.multa !== undefined ? formData.multa : ""} 
              onChange={(e) => setFormData({...formData, multa: e.target.value === "" ? null : parseFloat(e.target.value)})}
              onBlur={(e) => {
                if (e.target.value === "" || formData.multa === null) {
                  toast.warning("El valor mínimo para Multa debe ser 0");
                  setFormData({...formData, multa: 0});
                }
              }}
              placeholder="0.00"
              disabled={isPortal}
              className={`${isPortal ? "bg-slate-100" : ""} ${formData.clase_contribuyente === "Contribuyente con LAE" && formData.mes_seleccionado_tipo === "multa" ? "border-red-300 bg-red-50" : ""}`}
              data-testid="form-multa"
            />
          </div>
          <div className="space-y-2">
            <Label>Divisas ($)</Label>
            <Input 
              type="number"
              step="0.01"
              value={formData.divisas !== undefined ? formData.divisas.toFixed(2) : "0.00"} 
              readOnly
              className="bg-gray-100 font-semibold"
              placeholder="0.00"
              data-testid="form-divisas"
            />
            <p className="text-xs text-gray-500">Total (Bs.) / Dólar ({cotizacionActiva?.dolar?.toFixed(2) || "N/A"} Bs.)</p>
          </div>
          <div className="space-y-2">
            <Label>Divisas (€)</Label>
            <Input 
              type="number"
              step="0.01"
              value={formData.divisas_euro !== undefined ? formData.divisas_euro.toFixed(2) : "0.00"} 
              readOnly
              className="bg-gray-100 font-semibold"
              placeholder="0.00"
              data-testid="form-divisas-euro"
            />
            <p className="text-xs text-gray-500">Total (Bs.) / Euro ({cotizacionActiva?.euro?.toFixed(2) || "N/A"} Bs.)</p>
          </div>
          <div className="space-y-2">
            <Label>Total (Bs.)</Label>
            <Input 
              type="number"
              step="0.01"
              value={formData.total !== undefined ? formData.total.toFixed(2) : "0.00"} 
              readOnly
              className="bg-gray-100 font-semibold"
              placeholder="0.00"
              data-testid="form-total"
            />
            <p className="text-xs text-gray-500">Sub Total - Descuento - Reintegro - Retención + Multa</p>
          </div>

          {/* Carga de Documentos Soporte - Solo Portal del Contribuyente */}
          {isPortal && (
          <div className="col-span-2 space-y-3 mt-2 pt-4 border-t border-slate-200">
            <Label className="text-base font-semibold flex items-center gap-2">
              <Upload className="w-4 h-4 text-green-600" />
              Documentos Soporte
            </Label>
            <p className="text-xs text-slate-500">Cargue los documentos requeridos en formato PDF, JPG o PNG (máx. 10MB cada uno). Estos documentos serán enviados al submódulo "Cargar Documentos Soporte" para aprobación del funcionario.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {[
                { key: "libro_diario", label: "Libro Diario" },
                { key: "declaracion_iva", label: "Declaración de IVA" },
                { key: "otros", label: "Otros Documentos" }
              ].map(({ key, label }) => {
                const fileRef = formData[`archivo_${key}`];
                const uploading = formData[`uploading_${key}`];
                return (
                  <div key={key} className="border rounded-lg p-3 space-y-2 bg-slate-50" data-testid={`upload-section-${key}`}>
                    <Label className="text-sm font-medium">{label}</Label>
                    {fileRef ? (
                      <div className="flex items-center gap-2 text-green-600 text-sm">
                        <Check className="w-4 h-4" />
                        <span className="truncate">{fileRef.name || "Archivo cargado"}</span>
                      </div>
                    ) : (
                      <Input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        data-testid={`file-input-${key}`}
                        disabled={uploading}
                        className="text-xs"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            if (file.size > 10 * 1024 * 1024) {
                              toast.error("El archivo excede el tamaño máximo de 10MB");
                              return;
                            }
                            setFormData(prev => ({ ...prev, [`archivo_${key}`]: file }));
                            toast.success(`${label}: ${file.name} seleccionado`);
                          }
                        }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          )}

        </div>
      );
    }
    
    if (submodulo === "documentos-soporte") {
      return (
        <>
          <div className="space-y-2">
            <Label>Código</Label>
            <Input 
              value={formData.codigo || ""} 
              onChange={(e) => setFormData({...formData, codigo: e.target.value})}
              placeholder="Código del documento"
              data-testid="form-codigo"
            />
          </div>
          <div className="space-y-2">
            <Label>Tipo de Documento</Label>
            <Input 
              value={formData.tipo_documento || ""} 
              onChange={(e) => setFormData({...formData, tipo_documento: e.target.value})}
              placeholder="Ej: Factura, Comprobante, etc."
              data-testid="form-tipo-documento"
            />
          </div>
          <div className="space-y-2">
            <Label>Descripción</Label>
            <Textarea 
              value={formData.descripcion || ""} 
              onChange={(e) => setFormData({...formData, descripcion: e.target.value})}
              placeholder="Descripción del documento"
              data-testid="form-descripcion"
              rows={3}
            />
          </div>
        </>
      );
    }

    return null;
  };

  // Get table columns based on submodulo
  const getTableColumns = () => {
    if (submodulo === "datos-declaracion") {
      return [
        { key: "nro_planilla", header: "Nro Planilla" },
        { key: "clase_contribuyente", header: "Clase Contribuyente" },
        { key: "rif", header: "RIF" },
        { key: "nombre_razon_social", header: "Nombre / Razón Social" },
        { key: "lae", header: "LAE", render: (item) => {
          // Si tiene LAE, mostrarlo
          if (item.lae && item.lae !== "") {
            return <span className="text-slate-700">{item.lae}</span>;
          }
          // Si es contribuyente sin LAE, mostrar "No Aplica"
          if (item.clase_contribuyente === "Contribuyente sin LAE") {
            return <span className="text-slate-500 italic">No Aplica</span>;
          }
          // Si es contribuyente con LAE pero no tiene valor, mostrar "-"
          return <span className="text-slate-400">-</span>;
        }},
        { key: "codigo_catastral", header: "Código Catastral", render: (item) => (
          <span className={item.codigo_catastral ? "text-slate-700" : "text-slate-400"}>
            {item.codigo_catastral || "-"}
          </span>
        )},
        { key: "oficina", header: "Oficina", render: (item) => (
          <span className={(item.oficina_nombre || item.oficina) ? "text-slate-700" : "text-slate-400"}>
            {item.oficina_nombre || item.oficina || "-"}
          </span>
        )},
        { key: "fecha", header: "Fecha" },
        { key: "mes_anio_declaracion", header: "Mes/Año" },
        { key: "tipos_impuestos", header: "Tipos de Impuestos", render: (item) => {
          const tipos = item.tipos_impuestos || [];
          if (tipos.length === 0) return <span className="text-slate-400">-</span>;
          const tiposInfo = tipos.map(tipoId => {
            const tipo = tiposImpuesto.find(t => t.id === tipoId);
            return tipo ? tipo.nombre : null;
          }).filter(Boolean);
          return (
            <div className="flex flex-wrap gap-1">
              {tiposInfo.slice(0, 2).map((nombre, idx) => (
                <Badge key={idx} variant="outline" className="bg-cyan-50 text-cyan-700 border-cyan-200 text-xs">
                  {nombre}
                </Badge>
              ))}
              {tiposInfo.length > 2 && (
                <Badge variant="outline" className="bg-slate-100 text-slate-600 border-slate-200 text-xs">
                  +{tiposInfo.length - 2}
                </Badge>
              )}
            </div>
          );
        }},
        { key: "estatus_pago", header: "Estatus Pago", render: (item) => (
          <Badge variant="outline" className={item.estatus_pago === "Pagado" ? "bg-green-50 text-green-700 border-green-200" : "bg-amber-50 text-amber-700 border-amber-200"}>
            {item.estatus_pago || "Sin pagar"}
          </Badge>
        )},
        { key: "estatus", header: "Estatus", render: (item) => (
          <Badge variant="outline" className={item.estatus === "Anulada" ? "bg-red-50 text-red-700 border-red-200" : "bg-blue-50 text-blue-700 border-blue-200"}>
            {item.estatus || "Registrada"}
          </Badge>
        )},
        { key: "total", header: "Total", render: (item) => (
          <span className="font-medium">Bs. {(item.total || 0).toLocaleString('es-VE', { minimumFractionDigits: 2 })}</span>
        )}
      ];
    }
    
    if (submodulo === "documentos-soporte") {
      return [
        { key: "codigo", header: "Código" },
        { key: "tipo_documento", header: "Tipo Documento" },
        { key: "descripcion", header: "Descripción" },
        { key: "is_active", header: "Estado", render: (item) => (
          <Badge variant="outline" className={item.is_active !== false ? "bg-green-50 text-green-700 border-green-200" : "bg-red-50 text-red-700 border-red-200"}>
            {item.is_active !== false ? "Activo" : "Inactivo"}
          </Badge>
        )}
      ];
    }

    return [];
  };

  // Export functions
  const getExportFileName = () => {
    const fileNames = {
      "datos-declaracion": "datos_declaracion",
      "documentos-soporte": "documentos_soporte"
    };
    return fileNames[submodulo] || submodulo;
  };

  const getExportTitle = () => {
    return currentConfig?.title || "Liquidaciones";
  };

  const getExportData = () => {
    if (submodulo === "datos-declaracion") {
      return filteredData.map(item => ({
        "Nro Planilla": item.nro_planilla || "",
        "Clase Contribuyente": item.clase_contribuyente || "",
        "RIF": item.rif || "",
        "Fecha": item.fecha || "",
        "Mes/Año": item.mes_anio_declaracion || "",
        "Act. Económica": item.con_actividad_economica ? "Sí" : "No",
        "Oficina": item.oficina || "",
        "Descuento": item.descuento || 0,
        "Reintegro": item.reintegro || 0,
        "Retención": item.retencion || 0,
        "Multa": item.multa || 0,
        "Divisas ($)": item.divisas || 0,
        "Divisas (€)": item.divisas_euro || 0,
        "Total": item.total || 0
      }));
    }
    
    if (submodulo === "documentos-soporte") {
      return filteredData.map(item => ({
        "Código": item.codigo || "",
        "Tipo Documento": item.tipo_documento || "",
        "Descripción": item.descripcion || "",
        "Estado": item.is_active !== false ? "Activo" : "Inactivo"
      }));
    }

    return [];
  };

  const exportToCSV = () => {
    const exportData = getExportData();
    if (exportData.length === 0) {
      toast.error("No hay datos para exportar");
      return;
    }
    const headers = Object.keys(exportData[0]).join(",");
    const rows = exportData.map(row => Object.values(row).map(v => `"${v}"`).join(",")).join("\n");
    const csv = `${headers}\n${rows}`;
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    saveAs(blob, `${getExportFileName()}_${new Date().toISOString().split('T')[0]}.csv`);
    toast.success("Exportado a CSV exitosamente");
  };

  const exportToJSON = () => {
    const exportData = getExportData();
    if (exportData.length === 0) {
      toast.error("No hay datos para exportar");
      return;
    }
    const json = JSON.stringify(exportData, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    saveAs(blob, `${getExportFileName()}_${new Date().toISOString().split('T')[0]}.json`);
    toast.success("Exportado a JSON exitosamente");
  };

  const exportToExcel = () => {
    const exportData = getExportData();
    if (exportData.length === 0) {
      toast.error("No hay datos para exportar");
      return;
    }
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, getExportTitle().substring(0, 31));
    XLSX.writeFile(wb, `${getExportFileName()}_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success("Exportado a Excel exitosamente");
  };

  const exportToPDF = () => {
    const exportData = getExportData();
    if (exportData.length === 0) {
      toast.error("No hay datos para exportar");
      return;
    }
    const doc = new jsPDF('landscape');
    doc.setFontSize(16);
    doc.text(`Listado de ${getExportTitle()}`, 14, 20);
    doc.setFontSize(10);
    doc.text(`Fecha: ${new Date().toLocaleDateString('es-VE')}`, 14, 28);
    
    const headers = Object.keys(exportData[0]);
    const rows = exportData.map(row => Object.values(row));
    
    autoTable(doc, {
      head: [headers],
      body: rows,
      startY: 35,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [219, 39, 119] } // Pink color for Liquidaciones
    });
    
    doc.save(`${getExportFileName()}_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success("Exportado a PDF exitosamente");
  };

  const exportToHTML = () => {
    const exportData = getExportData();
    if (exportData.length === 0) {
      toast.error("No hay datos para exportar");
      return;
    }
    const headers = Object.keys(exportData[0]);
    const title = getExportTitle();
    let html = `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <title>Listado de ${title}</title>
  <style>
    body { font-family: Arial, sans-serif; padding: 20px; }
    h1 { color: #db2777; }
    table { border-collapse: collapse; width: 100%; margin-top: 20px; }
    th, td { border: 1px solid #ddd; padding: 12px; text-align: left; }
    th { background-color: #db2777; color: white; }
    tr:nth-child(even) { background-color: #f9fafb; }
    tr:hover { background-color: #fdf2f8; }
    .fecha { color: #6b7280; margin-bottom: 20px; }
  </style>
</head>
<body>
  <h1>Listado de ${title}</h1>
  <p class="fecha">Fecha: ${new Date().toLocaleDateString('es-VE')}</p>
  <table>
    <thead>
      <tr>${headers.map(h => `<th>${h}</th>`).join("")}</tr>
    </thead>
    <tbody>
      ${exportData.map(row => `<tr>${Object.values(row).map(v => `<td>${v}</td>`).join("")}</tr>`).join("")}
    </tbody>
  </table>
</body>
</html>`;
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    saveAs(blob, `${getExportFileName()}_${new Date().toISOString().split('T')[0]}.html`);
    toast.success("Exportado a HTML exitosamente");
  };

  const exportToText = () => {
    const exportData = getExportData();
    if (exportData.length === 0) {
      toast.error("No hay datos para exportar");
      return;
    }
    const headers = Object.keys(exportData[0]);
    const colWidths = headers.map(h => Math.max(h.length, ...exportData.map(row => String(row[h]).length)));
    const title = getExportTitle().toUpperCase();
    
    let text = `LISTADO DE ${title}\n`;
    text += `Fecha: ${new Date().toLocaleDateString('es-VE')}\n`;
    text += "=".repeat(Math.max(colWidths.reduce((a, b) => a + b + 3, 0), 50)) + "\n\n";
    
    text += headers.map((h, i) => h.padEnd(colWidths[i])).join(" | ") + "\n";
    text += colWidths.map(w => "-".repeat(w)).join("-+-") + "\n";
    
    exportData.forEach(row => {
      text += headers.map((h, i) => String(row[h]).padEnd(colWidths[i])).join(" | ") + "\n";
    });
    
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    saveAs(blob, `${getExportFileName()}_${new Date().toISOString().split('T')[0]}.txt`);
    toast.success("Exportado a Texto exitosamente");
  };

  // If no submodulo selected, show landing page (skip for portal mode)
  if (!isPortal && (!submodulo || !currentConfig)) {
    return (
      <div className="space-y-6" data-testid="liquidaciones-page">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center">
            <Calculator className="w-6 h-6 text-pink-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Liquidaciones
            </h1>
            <p className="text-slate-500">Seleccione un submódulo del menú lateral</p>
          </div>
        </div>

        <Card>
          <CardContent className="p-8">
            <div className="text-center">
              <Calculator className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-700 mb-2">Gestión de Liquidaciones</h3>
              <p className="text-slate-500 mb-6">
                Utilice el menú lateral para acceder a los diferentes submódulos de Liquidaciones.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
                {Object.entries(submoduloConfig).map(([key, config]) => {
                  const Icon = config.icon;
                  return (
                    <Button
                      key={key}
                      variant="outline"
                      className="flex flex-col items-center gap-2 h-auto py-6 hover:border-pink-300 hover:bg-pink-50/50"
                      onClick={() => navigate(`/liquidaciones/${key}`)}
                      data-testid={`btn-${key}`}
                    >
                      <div className={`w-12 h-12 bg-${config.color}-100 rounded-lg flex items-center justify-center`}>
                        <Icon className={`w-6 h-6 text-${config.color}-600`} />
                      </div>
                      <span className="text-sm font-medium text-center">{config.title}</span>
                      <span className="text-xs text-slate-500 text-center">{config.description}</span>
                    </Button>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const Icon = currentConfig.icon;
  const columns = getTableColumns();

  if (useBandejaPortal) {
    return <BandejaDocumentosSoporte />;
  }

  return (
    <div className="space-y-6" data-testid="liquidaciones-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 ${isPortal ? "bg-green-100" : `bg-${currentConfig.color}-100`} rounded-xl flex items-center justify-center`}>
            <Icon className={`w-6 h-6 ${isPortal ? "text-green-600" : `text-${currentConfig.color}-600`}`} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {isPortal ? "Declaraciones" : currentConfig.title}
            </h1>
            <p className="text-slate-500">{isPortal ? "Gestione sus declaraciones tributarias" : currentConfig.description}</p>
          </div>
        </div>
        <Button 
          variant="outline" 
          onClick={fetchData} 
          disabled={loading}
          className="gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Actualizar
        </Button>
      </div>

      {/* Content Card */}
      <Card>
        <CardContent className="p-4">
          {/* Toolbar */}
          <div className="flex items-center justify-between mb-4">
            {submodulo !== "datos-declaracion" ? (
              <div className="flex items-center gap-2 flex-1 max-w-sm">
                <Search className="w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Buscar..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="h-9"
                  data-testid="search-input"
                />
              </div>
            ) : (
              <div className="flex items-center gap-2">
                {/* Export Button - Left side for datos-declaracion (hidden for portal) */}
                {!isPortal && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="gap-2" data-testid="btn-exportar">
                      <Download className="w-4 h-4" />
                      Exportar
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    <DropdownMenuItem onClick={exportToHTML} className="gap-2 cursor-pointer">
                      <FileType className="w-4 h-4" />
                      HTML
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={exportToCSV} className="gap-2 cursor-pointer">
                      <FileText className="w-4 h-4" />
                      CSV
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={exportToText} className="gap-2 cursor-pointer">
                      <File className="w-4 h-4" />
                      Texto
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={exportToExcel} className="gap-2 cursor-pointer">
                      <FileSpreadsheet className="w-4 h-4" />
                      Excel
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={exportToPDF} className="gap-2 cursor-pointer">
                      <FileText className="w-4 h-4" />
                      PDF
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={exportToJSON} className="gap-2 cursor-pointer">
                      <FileJson className="w-4 h-4" />
                      JSON
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                )}
                
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button 
                      className="bg-pink-600 hover:bg-pink-700 gap-2"
                      onClick={openCreateDialog}
                      data-testid="btn-nuevo"
                    >
                      <Plus className="w-4 h-4" />
                      Nuevo
                    </Button>
                  </DialogTrigger>
                  <DialogContent className={submodulo === "datos-declaracion" ? "sm:max-w-3xl max-h-[90vh] overflow-y-auto" : "sm:max-w-md"}>
                    <DialogHeader>
                      <DialogTitle>
                        {editingItem ? `Editar ${isPortal ? "Declaración" : currentConfig.title}` : `Nueva ${isPortal ? "Declaración" : currentConfig.title}`}
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      {renderFormFields()}
                    </div>
                    <DialogFooter>
                      <DialogClose asChild>
                        <Button variant="outline">Cancelar</Button>
                      </DialogClose>
                      <Button 
                        className="bg-pink-600 hover:bg-pink-700"
                        onClick={editingItem ? handleUpdate : handleCreate}
                        data-testid="btn-guardar"
                      >
                        {editingItem ? "Actualizar" : "Guardar"}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
                
                <div className="border-l pl-2 ml-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={clearFilters}
                    className="gap-2"
                    data-testid="btn-limpiar-filtros"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Limpiar Filtros
                  </Button>
                </div>
                <span className="text-sm text-slate-500">
                  {filteredData.length} de {data.length} registros
                </span>
              </div>
            )}
            {submodulo !== "datos-declaracion" && (
              <div className="flex items-center gap-2">
                {/* Export Button */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="gap-2" data-testid="btn-exportar">
                      <Download className="w-4 h-4" />
                      Exportar
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={exportToHTML} className="gap-2 cursor-pointer">
                      <FileType className="w-4 h-4" />
                      HTML
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={exportToCSV} className="gap-2 cursor-pointer">
                      <FileText className="w-4 h-4" />
                      CSV
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={exportToText} className="gap-2 cursor-pointer">
                      <File className="w-4 h-4" />
                      Texto
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={exportToExcel} className="gap-2 cursor-pointer">
                      <FileSpreadsheet className="w-4 h-4" />
                      Excel
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={exportToPDF} className="gap-2 cursor-pointer">
                      <FileText className="w-4 h-4" />
                      PDF
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={exportToJSON} className="gap-2 cursor-pointer">
                      <FileJson className="w-4 h-4" />
                      JSON
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button 
                      className="bg-pink-600 hover:bg-pink-700 gap-2"
                      onClick={openCreateDialog}
                      data-testid="btn-nuevo"
                    >
                      <Plus className="w-4 h-4" />
                      Nuevo
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle>
                        {editingItem ? `Editar ${currentConfig.title}` : `Nuevo ${currentConfig.title}`}
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      {renderFormFields()}
                    </div>
                    <DialogFooter>
                      <DialogClose asChild>
                        <Button variant="outline">Cancelar</Button>
                      </DialogClose>
                      <Button 
                        className="bg-pink-600 hover:bg-pink-700"
                        onClick={editingItem ? handleUpdate : handleCreate}
                        data-testid="btn-guardar"
                      >
                        {editingItem ? "Actualizar" : "Guardar"}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            )}
              
            {/* Dialog de confirmación para Ingreso Bruto vacío */}
            <Dialog open={confirmIngresoBrutoOpen} onOpenChange={setConfirmIngresoBrutoOpen}>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle className="text-amber-600 flex items-center gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      Campo Ingreso Bruto Vacío
                    </DialogTitle>
                  </DialogHeader>
                  <div className="py-4">
                    <p className="text-slate-600 mb-4">
                      El valor mínimo de Ingreso Bruto debe ser 0.
                    </p>
                    <p className="text-slate-800 font-medium">
                      ¿Deseas agregar el valor 0 automáticamente?
                    </p>
                  </div>
                  <DialogFooter className="flex gap-2 sm:gap-0">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setConfirmIngresoBrutoOpen(false);
                        setPendingSaveAction(null);
                      }}
                      data-testid="btn-no-agregar-cero"
                    >
                      No
                    </Button>
                    <Button
                      onClick={confirmarAgregarCeroYGuardar}
                      className="bg-amber-600 hover:bg-amber-700"
                      data-testid="btn-si-agregar-cero"
                    >
                      Sí
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  {submodulo === "datos-declaracion" && (
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Acciones
                    </th>
                  )}
                  {columns.map((col, idx) => (
                    <th key={idx} className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      {col.header}
                    </th>
                  ))}
                  {submodulo !== "datos-declaracion" && (
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Acciones
                    </th>
                  )}
                </tr>
                {/* Filter row for datos-declaracion */}
                {submodulo === "datos-declaracion" && (
                  <tr className="border-b border-slate-200 bg-slate-100/50">
                    <td className="py-2 px-2">
                      {/* Actions column - no filter */}
                    </td>
                    <td className="py-2 px-2">
                      <Input
                        placeholder="Filtrar..."
                        value={filters.nro_planilla}
                        onChange={(e) => setFilters({...filters, nro_planilla: e.target.value})}
                        className="h-8 text-xs"
                        data-testid="filter-nro-planilla"
                      />
                    </td>
                    <td className="py-2 px-2">
                      <Input
                        placeholder="Filtrar..."
                        value={filters.clase_contribuyente}
                        onChange={(e) => setFilters({...filters, clase_contribuyente: e.target.value})}
                        className="h-8 text-xs"
                        data-testid="filter-clase"
                      />
                    </td>
                    <td className="py-2 px-2">
                      <Input
                        placeholder="Filtrar..."
                        value={filters.rif}
                        onChange={(e) => setFilters({...filters, rif: e.target.value})}
                        className="h-8 text-xs"
                        data-testid="filter-rif"
                      />
                    </td>
                    <td className="py-2 px-2">
                      <Input
                        placeholder="Filtrar..."
                        value={filters.nombre_razon_social}
                        onChange={(e) => setFilters({...filters, nombre_razon_social: e.target.value})}
                        className="h-8 text-xs"
                        data-testid="filter-nombre"
                      />
                    </td>
                    <td className="py-2 px-2">
                      <Input
                        placeholder="Filtrar..."
                        value={filters.lae}
                        onChange={(e) => setFilters({...filters, lae: e.target.value})}
                        className="h-8 text-xs"
                        data-testid="filter-lae"
                      />
                    </td>
                    <td className="py-2 px-2">
                      <Input
                        placeholder="Filtrar..."
                        value={filters.codigo_catastral}
                        onChange={(e) => setFilters({...filters, codigo_catastral: e.target.value})}
                        className="h-8 text-xs"
                        data-testid="filter-cod-catastral"
                      />
                    </td>
                    <td className="py-2 px-2">
                      <Input
                        placeholder="Filtrar..."
                        value={filters.fecha}
                        onChange={(e) => setFilters({...filters, fecha: e.target.value})}
                        className="h-8 text-xs"
                        data-testid="filter-fecha"
                      />
                    </td>
                    <td className="py-2 px-2">
                      <Input
                        placeholder="Filtrar..."
                        value={filters.mes_anio_declaracion}
                        onChange={(e) => setFilters({...filters, mes_anio_declaracion: e.target.value})}
                        className="h-8 text-xs"
                        data-testid="filter-mes-anio"
                      />
                    </td>
                    <td className="py-2 px-2">
                      {/* Skip tipos_impuestos - too complex for filter */}
                    </td>
                    <td className="py-2 px-2">
                      <Input
                        placeholder="Filtrar..."
                        value={filters.estatus_pago}
                        onChange={(e) => setFilters({...filters, estatus_pago: e.target.value})}
                        className="h-8 text-xs"
                        data-testid="filter-estatus-pago"
                      />
                    </td>
                    <td className="py-2 px-2">
                      <Input
                        placeholder="Filtrar..."
                        value={filters.estatus}
                        onChange={(e) => setFilters({...filters, estatus: e.target.value})}
                        className="h-8 text-xs"
                        data-testid="filter-estatus"
                      />
                    </td>
                    <td className="py-2 px-2">
                      {/* Oficina - sin filtro */}
                    </td>
                    <td className="py-2 px-2">
                      {/* Skip total - numeric filter */}
                    </td>
                  </tr>
                )}
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={columns.length + 1} className="py-8 text-center">
                      <div className="flex items-center justify-center gap-2 text-slate-500">
                        <RefreshCw className="w-5 h-5 animate-spin" />
                        Cargando...
                      </div>
                    </td>
                  </tr>
                ) : filteredData.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length + 1} className="py-8 text-center text-slate-500">
                      No hay registros. Los campos específicos se configurarán próximamente.
                    </td>
                  </tr>
                ) : (
                  filteredData.map((item, idx) => (
                    <tr key={item.id} className="border-b border-slate-100 hover:bg-pink-50/30 transition-colors" data-testid={`row-${idx}`}>
                      {/* Actions column on LEFT for datos-declaracion */}
                      {submodulo === "datos-declaracion" && (
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => handlePrintDeclaracion(item)}
                              className="h-8 w-8 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                              data-testid={`print-btn-${idx}`}
                              title="Imprimir planilla"
                            >
                              <Printer className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => openEditDialog(item)}
                              className="h-8 w-8 text-pink-600 hover:text-pink-800 hover:bg-pink-50"
                              data-testid={`edit-btn-${idx}`}
                              title="Editar registro"
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            {!isPortal && (
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => handleDelete(item.id)}
                              className="h-8 w-8 text-amber-600 hover:text-amber-800 hover:bg-amber-50"
                              data-testid={`delete-btn-${idx}`}
                              title="Anular registro"
                            >
                              <Ban className="w-4 h-4" />
                            </Button>
                            )}
                          </div>
                        </td>
                      )}
                      {columns.map((col, colIdx) => (
                        <td key={colIdx} className="py-3 px-4 text-sm text-slate-700">
                          {col.render ? col.render(item) : item[col.key]}
                        </td>
                      ))}
                      {/* Actions column on RIGHT for other submodulos */}
                      {submodulo !== "datos-declaracion" && (
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => openEditDialog(item)}
                              className="h-8 w-8 text-pink-600 hover:text-pink-800 hover:bg-pink-50"
                              data-testid={`edit-btn-${idx}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => handleDelete(item.id)}
                              className="h-8 w-8 text-red-600 hover:text-red-800 hover:bg-red-50"
                              data-testid={`delete-btn-${idx}`}
                              title="Eliminar registro"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LiquidacionesPage;
